import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import cn from 'classnames';
import emailMask from 'text-mask-addons/dist/emailMask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import faker from 'faker';
import get from 'lodash.get';
import pickBy from 'lodash/pickBy';

import Validator from 'components/Validator/Validator';
import Heading from 'components/Heading';
import GridLayout from 'components/Layout/GridLayout';
import Column from 'components/Layout/Column';
import FormGroup from 'components/Form/FormGroup';
import FormGroupLabel from 'components/FormGroupLabel';
import Input from 'components/Form/Input';
import MaskedInput from 'components/Form/MaskedInput';
import Select from 'components/Form/Select';
import Checkbox from 'components/Form/PopoverCheckbox';
import CheckButton from 'components/Form/Checkbox';
import { Button, VerifyButton } from 'components/Button';
import SecurityLock from 'components/Icons/SecurityLock';
import CirclePhone from 'components/Icons/CirclePhone';
import Popup from 'components/Popup';

import { useConfig } from 'config/ConfigContext';
import { checkinAction, getTestValuesData } from 'actions/workflow';

import comodo from 'assets/images/comodo.png';
import { verifyAddress } from 'utils/verifyAddress';
import { toTitleCase } from 'utils/toTitleCase';
import { unmask } from 'utils/unmask';

import {
  MERCHANT_APPLY_BUSINESS_TYPE_LIST as businessTypeList,
} from 'constants/formConstants';

import states from 'utils/states';
import schema from './schema';
import './style.scss';

const numberMask = createNumberMask({
  prefix: '',
  thousandsSeparatorSymbol: '',
  allowLeadingZeroes: true,
  integerLimit: 17,
});

const MerchantApply = (props) => {
  const { className, searchParams, validator: { setValues, values, errors, isValid, validate, onChangeHandler } } = props;
  const dispatch = useDispatch();
  const config = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isDifferentAddress, setIsDifferentAddress] = useState(true);
  const [isAddressPopupVisible, setIsAddressPopupVisible] = useState(false);
  const [isVerifyButtonClicked, setIsVerifyButtonClicked] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [popupType, setPopupType] = useState(null);
  const [prevPrincipalAddresses] = useState({});
  const [isBusinessTypeSelected, setIsBusinessTypeSelected] = useState(false);
  const [isBusinessAddressLoading, setIsBusinessAddressLoading] = useState(false);
  const [isBusinessAddressPopupVisible, setIsBusinessAddressPopupVisible] = useState(false);
  const [isBusinessVerified, setIsBusinessVerified] = useState(false);
  const [businessPopupType, setBusinessPopupType] = useState(null);
  const [isBusinessFirstLoad, setIsBusinessFirstLoad] = useState(true);
  const [isBusinessVerifyButtonClicked, setIsBusinessVerifyButtonClicked] = useState(false);
  const [prevBusinessAddresses, setPrevBusinessAddresses] = useState({});
  const [sameAsOwner, setSameAsOwner] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [errorResponse, setErrorResponse] = useState({});
  const [isFormValidFromService, setIsFormValidFromService] = useState(false);
  const [testSetValues, setTestSetValues] = useState(null);

  const form = useRef(null);

  const loadHandleSetValues = () => {
    dispatch(getTestValuesData({
      url: '/lookup/set-values-merchant-application',
      success: (res) => {
        setTestSetValues(res.default.attributes.merchant);
      },
      fail: (err) => {
        console.log('Error', err);
      },
    }));
  };

  useEffect(() => {
    if (searchParams.get('test')) {
      loadHandleSetValues();
    }
    if (searchParams.get('sid')) {
      localStorage.setItem('sid', searchParams.get('sid'));
    }
    if (localStorage.getItem('sid')) {
      dispatch(checkinAction({
        data: {
          accountId: localStorage.getItem('sid'),
        },
        url: '/salesforce/merchants/accountdetail',
        success: (response) => {
          const initialFormData = {
            channel: {
              code: 'dtm',
            },
            businessName: response.legalBusinessName,
            dba: response.dba,
            salesforceAccountId: localStorage.getItem('sid'),
            businessAddresses: {
              address: response.streetAddress,
              city: response.city,
              zipCode: response.zip,
              state: `${response.state}`.toUpperCase(),
              type: response.serviceType,
            },
            contacts: {
              firstName: response.contactFirstName,
              lastName: response.contactLastName,
              title: response.contactTitle,
              email: response.contactEmail,
              phoneNumber: response.phone,
            },
            email: response.contactEmail,
            principalOwner: {
              dateOfBirth: '01/01/1960',
            },
          };
          setValues(initialFormData);
        },
        fail: (error) => {
          console.log(error);
        },
      }));
    } else {
      const initialFormData = {
        merchant: {
          name: 'Dr.John Smith',
          phone: '111-111-1111',
        },
        channel: {
          code: 'dtm',
        },
      };
      setValues(initialFormData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isFormValidInternal = isValid && isFormValidFromService;
    let isSubmittedInternal = isSubmitted;
    const formErrorsInternal = {
      ...errorResponse,
      ...errors,
    };

    if (!formErrorsInternal['principalOwner.address.address'] && isSubmittedInternal && !isVerified) {
      formErrorsInternal['principalOwner.address.address'] = isVerifyButtonClicked ? 'Address is not valid, please enter valid address' : 'Please click verify Address';
      isFormValidInternal = false;
    }

    if (!formErrorsInternal['businessAddresses.address'] && isSubmittedInternal && !isBusinessVerified) {
      formErrorsInternal['businessAddresses.address'] = isBusinessVerifyButtonClicked ? 'Address is not valid, please enter valid address' : 'Please click verify Address';
      isFormValidInternal = false;
    }

    if (!formErrorsInternal['principalOwner.address.address'] && !isDifferentAddress) {
      formErrorsInternal['principalOwner.address.address'] = "Please confirm you've entered the Owner's HOME address, NOT business address.";
      isFormValidInternal = false;
    }

    if (form && !isFormValid && isSubmittedInternal) {
      const errs = Object.keys(formErrors);
      form[errs[0]] && form[errs[0]].focus();
      isSubmittedInternal = false;
    }

    setIsFormValid(isFormValidInternal);
    setIsSubmitted(isSubmittedInternal);
    setFormErrors(formErrorsInternal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusinessVerifyButtonClicked, isFormValidFromService, errors, errorResponse, isVerified, isVerifyButtonClicked, isBusinessVerified, isDifferentAddress, isValid]);

  const getCurrentDate = () => format(new Date(), 'MM/dd/yyyy');

  const checkDifferent = (addressData, type) => {
    let address = values && values.principalOwner && values.principalOwner.address ? values.principalOwner.address : {};
    let businessAddresses = values && values.businessAddresses ? values.businessAddresses : {};

    if (type === 'businessAddresses') {
      businessAddresses = { ...businessAddresses, ...addressData };
    } else {
      address = { ...address, ...addressData };
    }
    if (!address.address || !businessAddresses.address) {
      return;
    }
    const isDifferentAddressInternal = Object.keys(address).reduce(
      (res, key) => res || ((address[key] && address[key].toUpperCase()) !== (businessAddresses[key] && businessAddresses[key].toUpperCase())),
      false
    );
    setIsDifferentAddress(isDifferentAddressInternal);
  };

  const verifySmartyStreet = ({ address, city, state, zipcode, type }) => {
    switch (type) {
      case 'principalOwner':
        setIsAddressLoading(true);
        setIsVerifyButtonClicked(true);
        break;
      default:
        setIsBusinessAddressLoading(true);
        setIsBusinessVerifyButtonClicked(true);
    }

    verifyAddress({ zipcode, address, city, state }).then((response) => {
      if (response) {
        if (get(response, 'metadata.recordType') === 'P') {
          if (type === 'principalOwner') {
            setIsAddressPopupVisible(true);
            setIsAddressLoading(false);
            setIsVerified(false);
            setPopupType(3);
            setIsFirstLoad(false);
          } else {
            setIsBusinessAddressPopupVisible(true);
            setIsBusinessAddressLoading(false);
            setIsBusinessVerified(false);
            setBusinessPopupType(3);
            setIsBusinessFirstLoad(false);
          }
        } else {
          let addressData = {};
          switch (type) {
            case 'principalOwner':
              addressData = {
                ...values,
                principalOwner: {
                  ...values.principalOwner,
                  address: {
                    ...values.principalOwner.address,
                    address: response.deliveryLine1,
                    city: get(response, 'components.cityName'),
                    state: get(response, 'components.state'),
                    zipCode: `${response.deliveryPointBarcode}`.slice(0, 5),
                  },
                },
              };
              setIsVerified(true);
              setIsFirstLoad(false);
              setIsAddressLoading(false);
              setPrevBusinessAddresses({
                address: get(values, 'principalOwner.address.address'),
                city: get(values, 'principalOwner.address.city'),
                state: get(values, 'principalOwner.address.state'),
                zipCode: get(values, 'principalOwner.address.zipCode'),
              });
              break;
            default:
              addressData = {
                ...values,
                businessAddresses: {
                  ...values.businessAddresses,
                  address: response.deliveryLine1,
                  city: get(response, 'components.cityName'),
                  state: get(response, 'components.state'),
                  zipCode: `${response.deliveryPointBarcode}`.slice(0, 5),
                },
              };
              setIsBusinessVerified(true);
              setIsBusinessAddressLoading(false);
              setPrevBusinessAddresses({
                address: get(values, 'businessAddresses.address'),
                city: get(values, 'businessAddresses.city'),
                state: get(values, 'businessAddresses.state'),
                zipCode: get(values, 'businessAddresses.zipCode'),
              });
          }
          setValues(addressData);
        }
      } else {
        switch (type) {
          case 'principalOwner':
            setIsAddressPopupVisible(true);
            setIsAddressLoading(false);
            setIsVerified(false);
            setIsFirstLoad(false);
            setPopupType(2);
            break;
          default:
            setIsBusinessAddressPopupVisible(true);
            setIsBusinessAddressLoading(false);
            setIsBusinessVerified(false);
            setIsBusinessFirstLoad(false);
            setBusinessPopupType(2);
        }
      }
    }).catch((error) => {
      console.log('error', error);

      switch (type) {
        case 'principalOwner':
          setIsAddressLoading(false);
          setPopupType(1);
          setIsFirstLoad(false);
          setIsVerified(false);
          break;
        default:
          setIsBusinessAddressLoading(false);
          setBusinessPopupType(1);
          setIsBusinessFirstLoad(false);
          setIsBusinessVerified(false);
      }
    });
  };

  const handleSubmitForm = (data, e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setIsFormValidFromService(true);
    const isAddressValid = !config.smartyStreetEnforce ? isBusinessVerified : true;
    if (validate(schema).isValid && isAddressValid) {
      setIsLoading(true);

      const source = pickBy({
        ipAddress: props.ipAddress,
        url: window.location.href,
        affiliateId: searchParams.get('aid'),
        utm_source: searchParams.get('utm_source') ? decodeURI(searchParams.get('utm_source')) : searchParams.get('utm_source'),
        utm_medium: searchParams.get('utm_medium') ? decodeURI(searchParams.get('utm_medium')) : searchParams.get('utm_medium'),
        utm_term: searchParams.get('utm_term') ? decodeURI(searchParams.get('utm_term')) : searchParams.get('utm_term'),
        utm_content: searchParams.get('utm_content') ? decodeURI(searchParams.get('utm_content')) : searchParams.get('utm_content'),
        utm_campaign: searchParams.get('utm_campaign') ? decodeURI(searchParams.get('utm_campaign')) : searchParams.get('utm_campaign'),
      }, (item) => item !== undefined);

      const formData = {
        ...data,
        salesforceAccountId: localStorage.getItem('sid'),
        federalTaxIdOrSSN: unmask(data.federalTaxIdOrSSN),
        channel: {
          code: 'dtm',
          name: null,
          program: null,
          attributes: {},
        },
        purpose: {
          code: data.businessType === 'Funeral' ? 'funeral' : data.purpose.code,
        },
        businessAddresses: [{
          ...data.businessAddresses,
          country: 'US',
          isPrimary: true,
        }],
        contacts: [{
          ...data.contacts,
          phoneNumber: unmask(data.contacts.phoneNumber || ''),
        }],
        email: data.contacts.email,
        principalOwner: {
          ...data.principalOwner,
          address: {
            ...data.principalOwner.address,
            country: 'USA',
          },
          ssn: unmask(data.principalOwner.ssn),
        },
        yearsUnderCurrentOwnership: unmask(data.yearsUnderCurrentOwnership || ''),
        source,
        submittedDate: getCurrentDate(),
      };
      dispatch(checkinAction({
        data: formData,
        url: '/workflows/merchant/workflow/merchant-agreement/start',
        success: (response) => {
          const { navigate } = props;
          setIsLoading(false);
          if (!response.entityId) {
            navigate('/declined');
          } else {
            navigate('/congrats');
          }
        },
        fail: (error) => {
          const errorsObj = {};
          const errorsData = get(error, 'data.errors', []);

          errorsData.forEach((err) => {
            if (get(err, 'subject') === 'contacts[0].phoneNumber') {
              errorsObj['contacts.phoneNumber'] = get(err, 'message');
            } else {
              errorsObj[get(err, 'subject')] = get(err, 'message');
            }
          });
          setIsFormValidFromService(false);
          setErrorResponse({ ...errorsObj });
          setIsLoading(false);
        },
      }));
    } else {
      console.log('api error', errors);
    }
  };

  const initializeAddressState = (type) => {
    setIsSubmitted(false);
    switch (type) {
      case 'principalOwner':
        setIsVerified(false);
        setIsVerifyButtonClicked(false);
        break;
      default:
        setIsBusinessVerified(false);
        setIsBusinessVerifyButtonClicked(false);
    }
  };

  const handleInputChange = (event) => {
    switch (event.target.name) {
      case 'principalOwner.firstName':
      case 'principalOwner.lastName':
      case 'contacts.firstName':
      case 'contacts.lastName':
      case 'contacts.title':
      case 'submittedBy.name':
      case 'principalOwner.title':
      case 'accountHolderName':
        onChangeHandler(event.target.name, (event.target.value).replace(/[^a-zA-Z-' ]/g, ''));
        break;

      case 'principalOwner.address.state':
        initializeAddressState('principalOwner');
        checkDifferent({ state: event.target.value }, 'principalOwner');
        onChangeHandler(event.target.name, event.target.value);
        break;

      case 'principalOwner.address.zipCode':
        initializeAddressState('principalOwner');
        checkDifferent({ zipCode: event.target.value }, 'principalOwner');
        onChangeHandler(event.target.name, event.target.value);
        break;
      case 'principalOwner.address.address':
      case 'principalOwner.address.city':
        initializeAddressState('principalOwner');
        if (event.target.name === 'principalOwner.address.address') {
          checkDifferent({ address: (event.target.value).replace(/[^a-zA-Z0-9- ']/g, '') }, 'principalOwner');
        } else {
          checkDifferent({ city: (event.target.value).replace(/[^a-zA-Z0-9- ']/g, '') }, 'principalOwner');
        }
        onChangeHandler(event.target.name, (event.target.value).replace(/[^a-zA-Z0-9- ']/g, ''));
        break;

      case 'businessAddresses.state':
        initializeAddressState('businessAddresses');
        checkDifferent({ zipCode: event.target.value }, 'businessAddresses');
        onChangeHandler(event.target.name, event.target.value);
        break;
      case 'businessAddresses.zipCode':
        initializeAddressState('businessAddresses');
        checkDifferent({ zipCode: event.target.value }, 'businessAddresses');
        onChangeHandler(event.target.name, event.target.value);
        break;
      case 'businessAddresses.address':
      case 'businessAddresses.city':
        initializeAddressState('businessAddresses');
        if (event.target.name === 'businessAddresses.address') {
          checkDifferent({ address: (event.target.value).replace(/[^a-zA-Z0-9- ']/g, '') }, 'businessAddresses');
        } else {
          checkDifferent({ city: (event.target.value).replace(/[^a-zA-Z0-9- ']/g, '') }, 'businessAddresses');
        }
        onChangeHandler(event.target.name, (event.target.value).replace(/[^a-zA-Z0-9- ']/g, ''));
        break;
      case 'businessType':
        onChangeHandler(event.target.name, event.target.value);
        setIsBusinessTypeSelected(true);
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  };

  useEffect(() => {
    if (values.businessType === 'Funeral' || values.businessType === 'homeimprovement') {
      onChangeHandler('purpose.code', values.businessType);
    } else {
      onChangeHandler('purpose.code', '');
    }
  }, [values.businessType]);

  const handleBlur = (event) => {
    event.preventDefault();

    switch (event.target.name) {
      case 'businessAddresses.state':
        if (isBusinessFirstLoad && get(values, 'businessAddresses.city') && get(values, 'businessAddresses.address')) {
          verifySmartyStreet({
            zipcode: get(values, 'businessAddresses.zipCode'),
            address: get(values, 'businessAddresses.address'),
            city: get(values, 'businessAddresses.city'),
            state: event.target.value,
            type: 'businessAddresses',
          });
        }
        checkDifferent({ state: event.target.value }, 'businessAddresses');
        break;
      case 'businessAddresses.zipCode':
        if (isBusinessFirstLoad && get(values, 'businessAddresses.city') && get(values, 'businessAddresses.state') && get(values, 'businessAddresses.address')) {
          verifySmartyStreet({
            zipcode: event.target.value,
            address: get(values, 'businessAddresses.address'),
            city: get(values, 'businessAddresses.city'),
            state: get(values, 'businessAddresses.state'),
            type: 'businessAddresses',
          });
        }
        checkDifferent({ zipCode: event.target.value }, 'businessAddresses');
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'businessAddresses.address':
        if (isBusinessFirstLoad && get(values, 'businessAddresses.city') && get(values, 'businessAddresses.state')) {
          verifySmartyStreet({
            zipcode: get(values, 'businessAddresses.zipCode'),
            address: toTitleCase(event.target.value),
            city: get(values, 'businessAddresses.city'),
            state: get(values, 'businessAddresses.state'),
            type: 'businessAddresses',
          });
        }
        checkDifferent({ address: event.target.value }, 'businessAddresses');
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'businessAddresses.city':
        if (isBusinessFirstLoad && get(values, 'businessAddresses.address') && get(values, 'businessAddresses.state')) {
          verifySmartyStreet({
            zipcode: get(values, 'businessAddresses.zipCode'),
            address: get(values, 'businessAddresses.address'),
            city: toTitleCase(event.target.value),
            state: get(values, 'businessAddresses.state'),
            type: 'businessAddresses',
          });
        }
        checkDifferent({ city: event.target.value }, 'businessAddresses');
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;

      case 'principalOwner.address.state':
        if (isFirstLoad && get(values, 'principalOwner.address.city') && get(values, 'principalOwner.address.address')) {
          verifySmartyStreet({
            zipcode: get(values, 'principalOwner.address.zipCode'),
            address: get(values, 'principalOwner.address.address'),
            city: get(values, 'principalOwner.address.city'),
            state: event.target.value,
            type: 'principalOwner',
          });
        }
        checkDifferent({ state: event.target.value }, 'principalOwner');
        break;
      case 'principalOwner.address.zipCode':
        if (isFirstLoad && get(values, 'principalOwner.address.city') && get(values, 'principalOwner.address.state') && get(values, 'principalOwner.address.address')) {
          verifySmartyStreet({
            zipcode: event.target.value,
            address: get(values, 'principalOwner.address.address'),
            city: get(values, 'principalOwner.address.city'),
            state: get(values, 'principalOwner.address.state'),
            type: 'principalOwner',
          });
        }
        checkDifferent({ zipCode: event.target.value }, 'principalOwner');
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'principalOwner.address.address':
        if (isFirstLoad && get(values, 'principalOwner.address.city') && get(values, 'principalOwner.address.state')) {
          verifySmartyStreet({
            zipcode: get(values, 'principalOwner.address.zipCode'),
            address: toTitleCase(event.target.value),
            city: get(values, 'principalOwner.address.city'),
            state: get(values, 'principalOwner.address.state'),
            type: 'principalOwner',
          });
        }
        checkDifferent({ address: event.target.value }, 'principalOwner');
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'principalOwner.address.city':
        if (isFirstLoad && get(values, 'principalOwner.address.address') && get(values, 'principalOwner.address.state')) {
          verifySmartyStreet({
            zipcode: get(values, 'principalOwner.address.zipCode'),
            address: get(values, 'principalOwner.address.address'),
            city: toTitleCase(event.target.value),
            state: get(values, 'principalOwner.address.state'),
            type: 'principalOwner',
          });
        }
        checkDifferent({ city: event.target.value }, 'principalOwner');
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      default:
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
    }
  };

  const handleCheckboxChange = (name, value) => {
    onChangeHandler(name, value);
  };

  const handleRadioChange = (value) => {
    setSameAsOwner(value);

    if (value === true) {
      setValues({
        ...values,
        contacts: {
          firstName: values.principalOwner && values.principalOwner.firstName,
          lastName: values.principalOwner && values.principalOwner.lastName,
          email: values.principalOwner && values.principalOwner.email,
        },
      });
    } else {
      setValues({
        ...values,
        contacts: {
          firstName: undefined,
          lastName: undefined,
          email: undefined,
        },
      });
    }
  };

  const handleSetValues = (e) => {
    e.preventDefault();
    const { firstName, lastName } = {
      firstName: faker.name.lastName(),
      lastName: faker.name.firstName(),
    };
    setIsBusinessTypeSelected(true);
    const formValues = {
      businessName: `${lastName} ${faker.company.companyName()}`.toUpperCase(),
      ...testSetValues,
      principalOwner: {
        firstName,
        lastName,
        ...testSetValues.principalOwner,
      },
      contacts: {
        firstName,
        lastName,
        ...testSetValues.contacts,
      },
      submittedBy: {
        name: `${firstName} ${lastName}`,
      },
    };
    setValues(formValues);
    setTimeout(() => {
      setValues({
        ...formValues,
        purpose: {
          code: 'dental',
        },
      });
    }, 20);
  };

  const verifyBusinessAddress = (e) => {
    e.preventDefault();

    if (!get(values, 'businessAddresses.address')) {
      setIsBusinessAddressPopupVisible(true);
      setBusinessPopupType(1);
      setIsBusinessVerifyButtonClicked(true);
    } else {
      verifySmartyStreet({
        zipcode: get(values, 'businessAddresses.zipCode'),
        state: get(values, 'businessAddresses.state'),
        address: get(values, 'businessAddresses.address'),
        city: get(values, 'businessAddresses.city'),
        type: 'businessAddresses',
      });
    }
  };

  const verifyOwnerAddress = (e) => {
    e.preventDefault();

    if (!get(values, 'principalOwner.address.address')) {
      setIsAddressPopupVisible(true);
      setPopupType(1);
      setIsVerifyButtonClicked(true);
    } else {
      verifySmartyStreet({
        zipcode: get(values, 'principalOwner.address.zipCode'),
        state: get(values, 'principalOwner.address.state'),
        address: get(values, 'principalOwner.address.address'),
        city: get(values, 'principalOwner.address.city'),
        type: 'principalOwner',
      });
    }
  };

  const handleAbort = () => {
    setIsBusinessAddressPopupVisible(false);
    setIsAddressPopupVisible(false);
  };

  const handleOverride = () => {
    setIsBusinessAddressPopupVisible(true);
    setIsAddressPopupVisible(false);
  };

  const handleBusinessUndo = (e) => {
    e.preventDefault();

    const addressData = {
      ...values,
      businessAddresses: {
        ...values.businessAddresses,
        address: get(prevBusinessAddresses, 'address'),
        city: get(prevBusinessAddresses, 'city'),
        state: get(prevBusinessAddresses, 'state'),
        zipCode: get(prevBusinessAddresses, 'zipCode'),
      },
    };

    setIsBusinessVerified(false);
    setValues(addressData);
  };

  const handleUndo = (e) => {
    e.preventDefault();

    const addressData = {
      ...values,
      principalOwner: {
        ...values.principalOwner,
        address: {
          ...get(values, 'principalOwner.address'),
          address: get(prevPrincipalAddresses, 'address'),
          city: get(prevPrincipalAddresses, 'city'),
          state: get(prevPrincipalAddresses, 'state'),
          zipCode: get(prevPrincipalAddresses, 'zipCode'),
        },
      },
    };

    setIsVerified(false);
    setValues(addressData);
  };

  return (
    <form className={cn('App', className)} onSubmit={handleSubmitForm.bind(null, values)} ref={form}>
      {searchParams.get('test')
        && (
        <Button
          className={cn('button small w-50', 'button')}
          onClick={handleSetValues}
        >
          Set values
        </Button>
        )}
      <section className="container section">
        <div className="grid-container fluid merchants-apply">
          <div className="grid-container">
            <GridLayout className="grid-margin-x max-limited">
              <Heading
                heading="Merchant Enrollment Application"
                subHeading="Please tell us more about your business to get started"
              />

              <Column className="small-12 medium-12 large-8 main-column">
                <FormGroup>
                  <FormGroupLabel value="Business Information" />
                  <GridLayout className="grid-margin-x">
                    <Column className="small-12 medium-6">
                      <Input
                        label="Legal Business Name"
                        name="businessName"
                        value={get(values, 'businessName')}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors?.businessName}
                        errorMessage={formErrors?.businessName}
                        onBlur={handleBlur}
                        notification="Please provide your legal business name as registered with the state, including the entity type (ie. LLC or Inc.)."
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="D.B.A."
                        name="dba"
                        value={values.dba}
                        onChange={handleInputChange}
                        hasError={!!formErrors?.dba}
                        errorMessage={formErrors?.dba}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Select
                        name="businessType"
                        data={[
                          { value: 'Funeral', title: 'Funeral' },
                          { value: 'Medical', title: 'Medical' },
                          { value: 'Pet', title: 'Pet' },
                          { value: 'homeimprovement', title: 'Home Improvement' },
                          { value: 'Other', title: 'Other' },
                        ]}
                        value={values.businessType}
                        onChange={handleInputChange}
                        label="Business Category Type"
                        isRequired
                        hasError={!!formErrors?.businessType}
                        errorMessage={formErrors?.businessType}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      { isBusinessTypeSelected && values.businessType !== 'Funeral' && values.businessType !== 'homeimprovement' && values.businessType !== ''
                        && (
                        <Select
                          name="purpose.code"
                          data={businessTypeList[values.businessType] || []}
                          value={get(values, 'purpose.code')}
                          onChange={handleInputChange}
                          label="Product/Service Type"
                          isRequired
                          hasError={!!get(formErrors, 'purpose.code')}
                          errorMessage={formErrors?.businessType}
                        />
                        )}
                    </Column>
                    <Column className={cn('small-12 medium-12', 'positionRelative')}>
                      <Input
                        label="Street Address"
                        name="businessAddresses.address"
                        value={values.businessAddresses && values.businessAddresses.address}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['businessAddresses.address']}
                        errorMessage={formErrors['businessAddresses.address']}
                        onBlur={handleBlur}
                      />
                      <VerifyButton
                        onClick={isBusinessVerified ? handleBusinessUndo : verifyBusinessAddress}
                        isVerified={isBusinessVerified}
                        isLoading={isBusinessAddressLoading}
                      />
                      { isBusinessAddressPopupVisible
                        && (
                        <Popup
                          handleOverride={handleOverride}
                          handleAbort={handleAbort}
                          type={businessPopupType}
                          data={{
                            address: get(values, 'businessAddresses.address'),
                            city: get(values, 'businessAddresses.city'),
                            state: get(values, 'businessAddresses.state'),
                            zipcode: get(values, 'businessAddresses.zipCode'),
                          }}
                        />
                        )}
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="City"
                        name="businessAddresses.city"
                        value={values.businessAddresses && values.businessAddresses.city}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['businessAddresses.city']}
                        errorMessage={formErrors['businessAddresses.city']}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-7">
                      <GridLayout className="grid-margin-x">
                        <Column className="small-8">
                          <Select
                            name="businessAddresses.state"
                            data={states.usa}
                            value={values.businessAddresses && get(values, 'businessAddresses.state')}
                            onChange={handleInputChange}
                            label="State"
                            hasError={!!formErrors['businessAddresses.state']}
                            errorMessage={formErrors['businessAddresses.state']}
                            onBlur={handleBlur}
                            isRequired
                          />
                        </Column>
                        <Column className={cn('cell small-4', 'positionRelative')}>
                          <MaskedInput
                            label="Zip"
                            isMasked="99999"
                            name="businessAddresses.zipCode"
                            value={values.businessAddresses && values.businessAddresses.zipCode}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!formErrors['businessAddresses.zipCode']}
                            errorMessage={formErrors['businessAddresses.zipCode']}
                            isZipcode
                            onBlur={handleBlur}
                          />
                        </Column>
                      </GridLayout>
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Federal Tax ID#"
                        name="federalTaxIdOrSSN"
                        isMasked={[/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                        placeHolder="XX-XXXXXXX"
                        placeholderChar="X"
                        value={values.federalTaxIdOrSSN}
                        onChange={handleInputChange}
                        hasError={!!formErrors?.federalTaxIdOrSSN}
                        errorMessage={formErrors?.federalTaxIdOrSSN}
                        isRequired
                        onBlur={handleBlur}
                        notification="Please provide a Federal Tax ID, if your business has one. Otherwise, enter the principal owner's Social Security Number."
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Years Under Current Ownership"
                        name="yearsUnderCurrentOwnership"
                        isMasked={[/\d/, /\d/, /\d/]}
                        value={values.yearsUnderCurrentOwnership}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors?.yearsUnderCurrentOwnership}
                        errorMessage={formErrors?.yearsUnderCurrentOwnership}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Select
                        name="ownershipType"
                        data={[
                          { value: 'sole-proprietorship', title: 'Sole Proprietorship' },
                          { value: 'llc', title: 'Limited Liability Corp (LLC)' },
                          { value: 'corporation', title: 'Corporation' },
                          { value: 'partnership', title: 'Partnership' },
                        ]}
                        value={values.ownershipType}
                        onChange={handleInputChange}
                        label="Ownership Type"
                        isRequired
                        hasError={!!formErrors?.ownershipType}
                        errorMessage={formErrors?.ownershipType}
                      />
                    </Column>
                    <Column className="small-12 medium-6" />
                    <Column className="small-12 medium-6">
                      <Input
                        label="Bank name"
                        name="bankName"
                        value={values.bankName}
                        onChange={handleInputChange}
                        isRequired={!!(values.bankName || values.accountHolderName || values.accountNumber || values.routingNumber)}
                        hasError={!!formErrors?.bankName}
                        errorMessage={formErrors?.bankName}
                        onBlur={handleBlur}
                        isBadgeVisible={false}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Account Holder Name"
                        name="accountHolderName"
                        value={values.accountHolderName}
                        onChange={handleInputChange}
                        isRequired={!!(values.bankName || values.accountHolderName || values.accountNumber || values.routingNumber)}
                        hasError={!!formErrors?.accountHolderName}
                        errorMessage={formErrors?.accountHolderName}
                        onBlur={handleBlur}
                        isBadgeVisible={false}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Bank Account Number"
                        name="accountNumber"
                        isMasked={numberMask}
                        value={values.accountNumber}
                        onChange={handleInputChange}
                        isRequired={!!(values.bankName || values.accountHolderName || values.accountNumber || values.routingNumber)}
                        hasError={!!formErrors?.accountNumber}
                        errorMessage={formErrors?.accountNumber}
                        onBlur={handleBlur}
                        notification="Please provide Checking Account Information."
                        isBadgeVisible={false}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Bank Routing Number"
                        name="routingNumber"
                        isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                        value={values.routingNumber}
                        onChange={handleInputChange}
                        isRequired={!!(values.bankName || values.accountHolderName || values.accountNumber || values.routingNumber)}
                        hasError={!!formErrors?.routingNumber}
                        errorMessage={formErrors?.routingNumber}
                        onBlur={handleBlur}
                        isBadgeVisible={false}
                      />
                    </Column>
                  </GridLayout>
                </FormGroup>

                <FormGroup>
                  <FormGroupLabel value="Principal Owner Information" />
                  <GridLayout className="grid-margin-x">
                    <Column className="small-12 medium-6">
                      <Input
                        label="First Name"
                        name="principalOwner.firstName"
                        value={values.principalOwner && values.principalOwner.firstName}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['principalOwner.firstName']}
                        errorMessage={formErrors['principalOwner.firstName']}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Last Name"
                        name="principalOwner.lastName"
                        value={values.principalOwner && values.principalOwner.lastName}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['principalOwner.lastName']}
                        errorMessage={formErrors['principalOwner.lastName']}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Social Security Number"
                        name="principalOwner.ssn"
                        isMasked={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        placeHolder="XXX-XX-XXXX"
                        placeholderChar="X"
                        value={values.principalOwner && values.principalOwner.ssn}
                        onChange={handleInputChange}
                        hasError={!!formErrors['principalOwner.ssn']}
                        errorMessage={formErrors['principalOwner.ssn']}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Date of Birth"
                        name="principalOwner.dateOfBirth"
                        isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                        placeHolder="__/__/____"
                        value={values.principalOwner && values.principalOwner.dateOfBirth}
                        onChange={handleInputChange}
                        hasError={!!formErrors['principalOwner.dateOfBirth']}
                        errorMessage={formErrors['principalOwner.dateOfBirth']}
                        isRequired
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Email Address"
                        name="principalOwner.email"
                        isMasked={emailMask}
                        placeHolder="Email address"
                        value={values.principalOwner && values.principalOwner.email}
                        onChange={handleInputChange}
                        hasError={!!formErrors['principalOwner.email']}
                        errorMessage={formErrors['principalOwner.email']}
                        isRequired
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Phone Number"
                        name="principalOwner.phoneNumber"
                        isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        placeHolder="(___) ___-____"
                        value={values.principalOwner && values.principalOwner.phoneNumber}
                        onChange={handleInputChange}
                        hasError={!!formErrors['principalOwner.phoneNumber']}
                        errorMessage={formErrors['principalOwner.phoneNumber']}
                        isRequired
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className={cn('small-12 medium-12', 'positionRelative')}>
                      <Input
                        label="Street Address"
                        name="principalOwner.address.address"
                        value={values.principalOwner && values.principalOwner.address && values.principalOwner.address.address}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['principalOwner.address.address']}
                        errorMessage={formErrors['principalOwner.address.address']}
                        onBlur={handleBlur}
                        isBadgeVisible={isDifferentAddress}
                      />
                      <VerifyButton
                        onClick={isVerified ? handleUndo : verifyOwnerAddress}
                        isVerified={isVerified}
                        isLoading={isAddressLoading}
                      />
                      { isAddressPopupVisible
                        && (
                        <Popup
                          handleOverride={handleOverride}
                          handleAbort={handleAbort}
                          type={popupType}
                          data={{
                            address: get(values, 'principalOwner.address.address'),
                            city: get(values, 'principalOwner.address.city'),
                            state: get(values, 'principalOwner.address.state'),
                            zipcode: get(values, 'principalOwner.address.zipCode'),
                          }}
                        />
                        )}
                    </Column>
                    <Column className="small-12 medium-5">
                      <Input
                        label="City"
                        name="principalOwner.address.city"
                        value={values.principalOwner && values.principalOwner.address && values.principalOwner.address.city}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['principalOwner.address.city']}
                        errorMessage={formErrors['principalOwner.address.city']}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-7">
                      <GridLayout className="grid-margin-x">
                        <Column className="small-8">
                          <Select
                            name="principalOwner.address.state"
                            data={states.usa}
                            value={values.principalOwner && values.principalOwner.address && values.principalOwner.address.state}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            label="State"
                            isRequired
                            hasError={!!formErrors['principalOwner.address.state']}
                            errorMessage={formErrors['principalOwner.address.state']}
                          />
                        </Column>
                        <Column className={cn('small-4', 'positionRelative')}>
                          <MaskedInput
                            label="Zip"
                            name="principalOwner.address.zipCode"
                            isMasked="99999"
                            placeHolder="_____"
                            value={values.principalOwner && values.principalOwner.address && values.principalOwner.address.zipCode}
                            onChange={handleInputChange}
                            isRequired
                            isZipcode
                            hasError={!!formErrors['principalOwner.address.zipCode']}
                            errorMessage={formErrors['principalOwner.address.zipCode']}
                            onBlur={handleBlur}
                          />
                        </Column>
                      </GridLayout>
                    </Column>
                  </GridLayout>
                </FormGroup>

                <FormGroup>
                  <FormGroupLabel value="Primary Financing Contact" />
                  <p>Add the person who oversees financing at this location and will receive email to set up merchant portal login credentials.</p>
                  <GridLayout className="grid-margin-x">
                    <Column className="small-12">
                      <CheckButton
                        name="sameAsOwner"
                        id="same_as_owner"
                        label={['Same as Owner']}
                        onChange={handleRadioChange}
                        isChecked={sameAsOwner}
                      />
                    </Column>
                    <Column className="small-12 medium-6 large-4">
                      <Input
                        label="First Name"
                        name="contacts.firstName"
                        value={values.contacts && values.contacts.firstName}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['contacts.firstName']}
                        errorMessage={formErrors['contacts.firstName']}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6 large-4">
                      <Input
                        label="Last Name"
                        name="contacts.lastName"
                        value={values.contacts && values.contacts.lastName}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['contacts.lastName']}
                        errorMessage={formErrors['contacts.lastName']}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6 large-4">
                      <Input
                        label="Job Title"
                        name="contacts.title"
                        value={values.contacts && values.contacts.title}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['contacts.title']}
                        errorMessage={formErrors['contacts.title']}
                        onBlur={handleBlur}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Email Address"
                        name="contacts.email"
                        isMasked={emailMask}
                        placeHolder="Email address"
                        value={values.contacts && values.contacts.email}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['contacts.email']}
                        errorMessage={formErrors['contacts.email']}
                      />
                    </Column>
                    <Column className="small-12 medium-6">
                      <Input
                        label="Main Office Phone #"
                        isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        placeHolder="(___) ___-____"
                        name="contacts.phoneNumber"
                        value={values.contacts && values.contacts.phoneNumber}
                        onChange={handleInputChange}
                        isRequired
                        hasError={!!formErrors['contacts.phoneNumber']}
                        errorMessage={formErrors['contacts.phoneNumber']}
                      />
                    </Column>
                  </GridLayout>
                </FormGroup>
              </Column>

              {/* <!-- Merchanys Apply Sidebar --> */}
              <Column id="Sidebar" className="small-12 large-4 sidebar-apply show-for-large" data-sticky-container>
                <div className="sticky" data-sticky data-top-anchor="Sidebar" data-btm-anchor="Footer" data-margin-top="7.5">
                  <div className="card">
                    <div className="card-section">
                      <h5>Need Help?</h5>
                      <ul className={cn('sidebar-help')}>
                        <li>
                          <a href="tel:18009946177">
                            <CirclePhone className="svg-icon" />
                            800-994-6177
                          </a>
                        </li>
                      </ul>
                    </div>
                    <div className="card-section sidebar-testimonials">
                      <h5>Testimonials</h5>
                      <p className="p-small">&quot;LendingUSA has allowed us to help more of our patients. Their financing program helps them find a payment option that works for them, and the whole process is extremely fast for our practice. We are glad to be working with them.&quot;</p>
                      <small>–  Dr. Amin Nia, Spectrum Medical Group</small>
                    </div>
                  </div>

                  <GridLayout className="security">
                    <Column className={cn('small-9 ssl', 'encryption')}>
                      <SecurityLock width="28" className="securityLock" />
                      <p className={cn('p-small', 'noBottomMargin')}>
                        <strong>128-bit SSL</strong>
                        {' '}
                        protection and strict encryption
                      </p>
                    </Column>
                    <Column className="small-3 certificate">
                      <img src={comodo} alt="" className="sectigo-image" />
                    </Column>
                  </GridLayout>
                </div>
              </Column>
              {/* <!-- End Merchanys Apply Sidebar --> */}
              <FormGroup>
                <h4>Your Signature</h4>
                <GridLayout className="grid-margin-x">
                  <Column className="small-12">
                    <textarea className="signatureTerm" rows="11" placeholder="None" readOnly defaultValue="Electronic Signature Agreement. By selecting the &quot;Submit Application&quot; button, you are signing this Merchant Enrollment Application (&quot;Application&quot;) electronically. You agree your electronic signature is the legal equivalent of your manual signature on this Application. By selecting &quot;Submit Application&quot; you consent to be legally bound by LendingUSA, LLC's (&quot;LendingUSA&quot;) terms and conditions. You further agree that your use of a key pad, mouse or other device to select an item, button, icon or similar act/action on lendingusa.com or any of its affiliated websites constitutes your signature (hereafter referred to as &quot;E-Signature&quot;). You also agree that no third-party verification is necessary to validate your E-Signature, and that the lack of such third-party verification will not in any way affect the enforceability of your E-Signature, or of any resulting agreement between you and LendingUSA. You also certify to LendingUSA that you are the duly authorized representative of the business and are authorized to provide the information and submit the Application on behalf of the business. You further understand and acknowledge that your submission of the Application will bind the owner and/or business to LendingUSA's policies and terms and conditions as contained on LendingUSA.com's website and as disclosed to you in this Application as of the date of your E-Signature." />
                  </Column>
                  <Column className="small-12" style={{ marginTop: '30px', marginBottom: '30px', marginLeft: '30px' }}>
                    <Column className="small-12 checkbox-row">
                      <Checkbox
                        label={['Check here to agree to our Merchant Agreement']}
                        name="merchantAgreement"
                        onChange={handleCheckboxChange.bind(null, 'merchantAgreement')}
                        isChecked={values.merchantAgreement}
                        id="merchantAgreement"
                        errorMessage={formErrors?.merchantAgreement}
                      >
                        <div>
                          <p style={{ marginBottom: '0' }}>&nbsp;</p>
                        </div>

                        <div style={{ paddingRight: 20 }}>
                          <p style={{ textAlign: 'right', fontStyle: 'italic' }}>
                            Proprietary & Confidential
                          </p>
                        </div>

                        <p style={{ textAlign: 'center' }}>
                          <b>LendingUSA, LLC</b>
                        </p>

                        <p style={{ textAlign: 'center' }}>
                          <b>Merchant Agreement</b>
                        </p>

                        <p>
                          By executing an Application (as defined herein) to participate in the LendingUSA Merchant Program (“
                          <u>Program</u>
                          ”), Merchant accepts and agrees to be bound by the terms and conditions contained in this LendingUSA Merchant Agreement (“
                          <u>Agreement</u>
                          ”) as follows:
                          <br />
                          <br />
                          This Agreement is between LendingUSA, LLC, together with its affiliates, permitted assigns and permitted designees, acting as program administrator (
                          <u>“Program Administrator”</u>
                          ) and the undersigned business entity (the “
                          <u>Merchant</u>
                          ”). This Agreement sets forth the terms and conditions under which Program Administrator authorizes Merchant to participate in the Program and is effective as of the date Merchant electronically signs this Agreement (“
                          <u>Effective Date</u>
                          ”). Program Administrator and Merchant each may be referred to herein as a “Party” and collectively as the “Parties.”
                          <br />
                          <br />

                          Capitalized words not otherwise defined herein have the meanings set forth in
                          &nbsp;
                          <u>Section 1</u>
                          &nbsp;
                          of this Agreement.
                        </p>

                        <p style={{ textAlign: 'left' }}>
                          <b>BY ELECTRONICALLY SIGNING THIS AGREEMENT, MERCHANT HAS SIGNIFIED ITS AGREEMENT TO THESE TERMS.</b>
                        </p>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">1.</span>
                              &nbsp;
                              <b>
                                Definitions.
                              </b>
                            </p>
                          </li>
                          <li>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">1.1.</span>
                                  &nbsp;“
                                  <u>Acceptable Identification</u>
                                  ” means an original form of identification that contains a photograph and that is legally recognized and accepted by the government of the state in which Merchant is to use the Merchant Platform (defined herein) to submit the Loan Application (as defined herein), including a state-issued driver’s license or other state-issued identification. Acceptable Identification must validate that the Customer (as defined herein) is at least 18 years of age. “Acceptable Identification” does not include identification issued by any military agency or US Citizenship and Immigration Services, or state-issued identification that is expressly limited to non-identification purposes (such as an AB—60 driver’s license, which entitles California residents to driving privileges but does not constitute a valid form of identification). Program Administrator reserves the right to amend the type of identification which constitutes Acceptable Identification.
                                </p>
                                <p>
                                  <span className="count">1.2.</span>
                                  &nbsp;“
                                  <u>Affiliate</u>
                                  ” is a person that, directly or indirectly, controls, or is controlled by, or is under common control with, Merchant. For purposes of this definition of Affiliate, “
                                  <u>control</u>
                                  ” shall mean the possession, direct or indirect, of the power to direct or cause the direction of the management and policies of a person, whether through the ownership of voting shares, by contract, or otherwise.
                                </p>
                                <p>
                                  <span className="count">1.3.</span>
                                  &nbsp;“
                                  <u>Application</u>
                                  ” is the required form that must be submitted by Merchant to Program Administrator to assess its qualification to enter into the Program.
                                </p>
                                <p>
                                  <span className="count">1.4.</span>
                                  &nbsp;“
                                  <u>Business Day</u>
                                  ” is any day that is not a Saturday, a Sunday or a federally recognized holiday observed in the state of California.
                                </p>
                                <p>
                                  <span className="count">1.5.</span>
                                  &nbsp;“
                                  <u>Borrower</u>
                                  ” is a customer of Merchant who has applied for and has been approved for a Loan (defined herein).
                                </p>
                                <p>
                                  <span className="count">1.6.</span>
                                  &nbsp;“
                                  <u>Customer</u>
                                  ” is a qualifying consumer seeking to finance the purchase of certain goods or services offered and sold by Merchant.
                                </p>
                                <p>
                                  <span className="count">1.7.</span>
                                  &nbsp;“
                                  <u>Funding Participant</u>
                                  ” is the federally insured financial institution and other persons, together with their Affiliates, permitted assigns and permitted designees, including their respective servicers and Sponsors (defined herein), that originated the Loan or holds title and/or economic rights to the Loan originated through the Program and/or the Loan Program (as defined herein). Funding Participant includes any purchaser of an economic interest in the Loan, and their Sponsor, if applicable.
                                </p>
                                <p>
                                  <span className="count">1.8.</span>
                                  &nbsp;“
                                  <u>Guidelines</u>
                                  ” are any program requirements, guidelines, instructions or procedures that Program Administrator communicates to Merchant and updates from time to time.
                                </p>
                                <p>
                                  <span className="count">1.9.</span>
                                  &nbsp;“
                                  <u>Loan</u>
                                  ” is a loan to a Borrower created pursuant to the Loan Program.
                                </p>
                                <p>
                                  <span className="count">1.10.</span>
                                  &nbsp;“
                                  <u>Loan Agreement</u>
                                  ” is a written agreement between a Funding Participant and a Borrower containing the terms and conditions of a Loan.
                                </p>
                                <p>
                                  <span className="count">1.11.</span>
                                  &nbsp;“
                                  <u>Loan Application</u>
                                  ” is the required form that must be submitted by Customers to Program Administrator whereby the Customer signifies his/her consent to terms, conditions and disclosures with respect to his/her request for credit, request for prequalification, or inquiry about credit products offered through Program Administrator.
                                </p>
                                <p>
                                  <span className="count">1.12.</span>
                                  &nbsp;“
                                  <u>Loan Documents</u>
                                  ” are the Loan Agreement, associated Truth-in-Lending Act disclosures and other documentation and communications from a Funding Participant (including documentation regarding online Borrower accounts and describing the rights of Program Administrator and Funding Participants to collect from past due Borrowers).
                                </p>
                                <p>
                                  <span className="count">1.13.</span>
                                  &nbsp;“
                                  <u>Merchant</u>
                                  ” is the person named as Merchant in the Application and, for purposes of Section 17 and Section 18 with respect to a Merchant that is not publicly-traded, shall expressly include all persons who, directly or indirectly, have an ownership interest in Merchant (and, by participating in the Program, Merchant represents and warrants that all authorizations and approvals of any such persons necessary for them to be included in the definition of Merchant for such purpose have been obtained).
                                </p>
                                <p>
                                  <span className="count">1.14.</span>
                                  &nbsp;“
                                  <u>Person</u>
                                  ” is an individual, corporation, partnership, limited liability company, joint venture, association, trust, unincorporated organization or other entity.
                                </p>
                                <p>
                                  <span className="count">1.15.</span>
                                  &nbsp;“
                                  <u>Program Administrator</u>
                                  ” is LendingUSA, LLC, together with its Affiliates, permitted assigns and permitted designees, acting as program administrator for the Program, a Delaware limited liability company having a principal place of business at 15303 Ventura Boulevard, Suite 850, Sherman Oaks, California 91403.
                                </p>
                                <p>
                                  <span className="count">1.16.</span>
                                  &nbsp;“
                                  <u>Sponsor</u>
                                  ” means, with respect to any applicable Funding Participant, the servicer (including its affiliates, where applicable) who provides such Funding Participant Loan Application processing services.
                                </p>
                                <p>
                                  <span className="count">1.17.</span>
                                  &nbsp;“
                                  <u>Transaction Request</u>
                                  ” means evidence of a sale in paper or electronic form of products and services purchased from Merchant by a Borrower and shall include any and all information required by this Agreement.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">2.</span>
                              &nbsp;
                              <b>
                                Overview of the Program.
                              </b>
                            </p>

                            <ul>
                              <li>
                                <p>
                                  <span className="count">2.1.</span>
                                  &nbsp;Program Administrator, among other things, through its relationship with Funding Participants, provides administrative, technical, and ministerial services to Funding Participants and facilitates the offering of certain consumer financing options, including but not limited to closed-end installment Loans (“
                                  <u>Loan Program</u>
                                  ”).
                                </p>
                              </li>

                              <li>
                                <p>
                                  <span className="count">2.2.</span>
                                  &nbsp;Merchant agrees to participate in the Program in accordance with the terms and conditions of this Agreement, including all Guidelines.
                                </p>
                              </li>

                              <li>
                                <p>
                                  <span className="count">2.3.</span>
                                  &nbsp;Merchant wishes to assist Customers in obtaining consumer financing options through Program Administrator’s proprietary platform (“
                                  <u>Merchant Platform</u>
                                  ”) and related application programming interfaces (“
                                  <u>APIs</u>
                                  ”) that are part of the Program.
                                </p>
                              </li>

                              <li>
                                <p>
                                  <span className="count">2.4.</span>
                                  &nbsp;Provided that Program Administrator approves the Application, Merchant will be authorized to participate in the Program by assisting Customers to complete and submit a Loan Application or enter into Loan Agreement through the Merchant Platform.
                                </p>
                              </li>

                              <li>
                                <p>
                                  <span className="count">2.5.</span>
                                  &nbsp;Funding Participant is a third-party beneficiary of this Agreement with respect to any Loan Application submitted to, or Loan Agreement entered into by, such Funding Participant, including, where applicable its Sponsors, successors and assigns.  As such, each third-party beneficiary has, and may directly enforce, all rights and remedies of Program Administrator under this Agreement with respect to such Loan Applications or Loan Agreement as if a party to this Agreement, regardless of whether such Funding Participant, including its Sponsor, successors or assigns, is referenced in an applicable Agreement provision.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">3.</span>
                              &nbsp;
                              <b>
                                Terms and Conditions of Merchant Participation.
                              </b>
                            </p>
                          </li>
                        </ul>

                        <ul className="list">
                          <ul>
                            <li>
                              <p>
                                <span className="count">3.1.</span>
                                &nbsp;Merchant acknowledges and agrees that this Agreement is contingent upon the evaluation and approval of Merchant by Program Administrator for participation in the Program, in Program Administrator’s sole discretion.  Such evaluation may include, but is not limited to, evaluating any history of litigation and customer complaints, historic chargeback rates and losses, tax returns, legal status, the financial background of Merchant’s owner (including credit report or other background information), the overall financial health of Merchant, and any other information reasonably requested by Program Administrator.  Program Administrator reserves the right, in its reasonable discretion, to periodically reassess Merchant’s eligibility for participation in the Program.  Merchant agrees to promptly provide any information requested by Program Administrator to assess Merchant’s eligibility for participation in the Program, both initially and periodically, and to verify compliance with this Agreement.
                              </p>
                            </li>

                            <li>
                              <p>
                                <span className="count">3.2.</span>
                                &nbsp;Merchant acknowledges and agrees that Funding Participant is solely responsible for establishing (i) the credit criteria for evaluating and making approval or denial decisions on each Loan Application, (ii) the interest rate(s), fees and all other terms and conditions of the Loan Agreement, and (iii) any terms and conditions governing dispersal of Loan Proceeds (defined below) to Merchant, including applicable Funding Percentages (as defined herein).
                              </p>
                            </li>

                            <li>
                              <p>
                                <span className="count">3.3.</span>
                                &nbsp;Merchant acknowledges and agrees to:
                              </p>
                              <ul>
                                <li>
                                  <p>
                                    <span className="count">3.3.1.</span>
                                    &nbsp;adhere to the Program Administrator&apos;s Terms of Use – Merchant as in effect from time to time, incorporated herein by reference, and located at
                                    {' '}
                                    <a href="https://www.lendingusa.com/terms-of-use/"><u>https://www.lendingusa.com/terms-of-use/</u></a>
                                    ;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.2.</span>
                                    &nbsp;adhere to the Privacy Policy as in effect from time to time, incorporated herein by reference, and located at
                                    {' '}
                                    <a href="https://www.lendingusa.com/privacy-policy/"><u>https://www.lendingusa.com/privacy-policy/</u></a>
                                    ;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.3.</span>
                                    &nbsp;adhere to the Guidelines, as in effect from time to time, incorporated herein by reference;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.4.</span>
                                    &nbsp;promote the Program in a legally compliant, accurate, complete, unbiased, non-misleading, and fair manner;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.5.</span>
                                    &nbsp;ensure that Loan Proceeds are used only in connection with their intended use for eligible products and services;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.6.</span>
                                    &nbsp;ensure all employees or agents involved with the Program are advised of the requirements related to offering the Program to Merchant’s customers, and that Merchant’s employees or agents having sales and finance responsibilities have completed the orientation and training related to the Program adopted by Funding Participant and provided by Program Administrator;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.7.</span>
                                    &nbsp;take commercially reasonable measures to prevent fraudulent activity by a consumer, Customer, Borrower or Merchant (including any of its employees, agents or subcontractors) with respect to a Loan Application, a Loan or Loan Proceeds;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.8.</span>
                                    &nbsp;cooperate with Program Administrator in investigating and remediating escalations, complaints and disputes about Merchant, and responding within five (5) Business Days or such shorter time as required by this Agreement to any request for information, audit or review related to Merchant’s participation in the Program;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.9.</span>
                                    &nbsp;maintain any information Merchant receives regarding each Borrower or prospective Borrower (in such capacities) or a Loan as strictly confidential;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.10.</span>
                                    &nbsp;provide to Program Administrator in a timely manner all information relating to Merchant’s participation in the Program and any Borrower and prospective Borrower as set forth herein and otherwise requested by Program Administrator or a Funding Participant;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.11.</span>
                                    &nbsp;respond within five (5) Business Days, or such shorter time as required by this Agreement, to any inquiry from Program Administrator, and fully cooperate with Program Administrator in connection with the resolution of any dispute involving a Borrower or prospective Borrower; and
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.3.12.</span>
                                    &nbsp;with respect to any documents or forms provided to or to be executed by a Borrower or prospective Borrower, or which constitute a disclosure required by Program Administrator or under applicable law in connection with the Program, only use such documents and forms provided to Merchant, or approved in writing by, Program Administrator (and only using the latest version thereof) and not modify any such documents or forms without Program Administrator’s prior written consent.
                                  </p>
                                </li>
                              </ul>
                            </li>
                            <li>
                              <p>
                                <span className="count">3.4.</span>
                                &nbsp;If Merchant is also participating in Program Administrator’s Deposit Program (as defined below),  then the following also applies:
                              </p>
                              <ul>
                                <li>
                                  <p>
                                    <span className="count">3.4.1.</span>
                                    &nbsp;&quot;
                                    <u>Deposit</u>
                                    &quot; means the sum of money initially paid or collected from the Customer towards the financing of the purchase of goods and/or services offered by the Merchant;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.2.</span>
                                    &nbsp;“
                                    <u>Deposit Program</u>
                                    ” refers to an arrangement within the Program whereby a Deposit is collected from the Customer;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.3.</span>
                                    &nbsp;“
                                    <u>Deposit Payout</u>
                                    ” refers to the payment of the Deposit to Merchant’s Settlement Account (defined herein);
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.4.</span>
                                    &nbsp;Merchant hereby designates the applicable Funding Participant as its agent for the purposes of collecting and/or refunding the Deposit, or any part thereof;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.5</span>
                                    &nbsp;Merchant understands that the applicable Funding Participant may utilize authorized third parties to collect or refund any Deposits, or any parts thereof, on their behalf;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.6</span>
                                    &nbsp;If a Deposit is collected, Merchant understands that the Deposit Payout will not be disbursed to Merchant’s Settlement Account until its Customer signs a Loan Agreement;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.7</span>
                                    &nbsp;If the Customer (i) fails to sign a Loan Agreement prior to the Loan Application’s expiration date, (ii) requests a refund of the Deposit prior to signing a Loan Agreement, or (iii) is otherwise entitled to a refund of the Deposit either before or after the Customer signs a Loan Agreement but prior to the Deposit Payout being disbursed to Merchant’s Settlement Account, Merchant hereby authorizes the applicable Funding Participant to refund the Deposit, or any part thereof, back to its Customer;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.8</span>
                                    &nbsp;If the Deposit Payout has already been disbursed by the applicable Funding Participant to Merchant’s Settlement Account, and Merchant’s Customer, now Borrower, is owed a refund of the Deposit or any part thereof, then Merchant authorizes Funding Participant to withdraw the Deposit Payout or any part thereof, from Merchant’s Settlement Account and refund the Deposit, or any part thereof, to Borrower;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.9</span>
                                    &nbsp;If a Deposit Payout and Loan Proceeds have been disbursed to Merchant’s Settlement Account and a refund is due to the Borrower, Funding Participant may apply Deposit, or any part thereof, to Borrower’s outstanding loan balance in lieu of returning the Deposit, or any part thereof, directly to Borrower;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.10</span>
                                    &nbsp;Though Funding Participant will facilitate the collection and refunding of Deposits, or any part thereof, as outlined above, Merchant is solely responsible for the return of a Deposit, or any part thereof, to Customer and/or Borrower; and
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.4.11</span>
                                    &nbsp;In the event Customer and/or Borrower initiates a chargeback of the Deposit through their financial institution, Merchant (i) shall reimburse Funding Participant for any assessed fees related to the chargeback, (ii) authorizes Funding Participant to return the Deposit to the Customer and/or Borrower if the Deposit Payout has not been Disbursed to Merchant’s Settlement Account, (iii) authorizes Funding Participant to debit Merchant’s Settlement Account for the amount of the chargeback, up to the amount of the Deposit Payout, if the Deposit Payout has been disbursed to Merchant, and to keep those funds as reimbursement if the Customer’s and/or Borrower’s financial institution ultimately agrees with Customer and/or Borrower with regards to the chargeback. For the avoidance of doubt, Merchant will be responsible for all chargebacks initiated by Customers and/or Borrowers through their financial institutions to recover the Deposit, or any part thereof.
                                  </p>
                                </li>
                              </ul>
                            </li>
                            <li>
                              <p>
                                <span className="count">3.5.</span>
                                &nbsp;In addition to Merchant’s other obligations, Merchant agrees that the following types of items are ineligible for financing through the Program (“
                                <u>Prohibited Items</u>
                                ”):
                              </p>
                              <ul>
                                <li>
                                  <p>
                                    <span className="count">3.5.1.</span>
                                    &nbsp;Illegal or illicit items, activities or transactions;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.5.2.</span>
                                    &nbsp;Ammunition, firearms, certain firearm parts or accessories, and certain weapons or knives regulated under applicable law;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.5.3.</span>
                                    &nbsp;Illegal narcotics, steroids, controlled substances, or drug paraphernalia, including any narcotics, steroids, controlled substances, or drug paraphernalia not prescribed by a properly licensed physician or other healthcare provider capable of making such prescriptions;
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.5.4.</span>
                                    &nbsp;Goods or services where the total purchase price is less than one thousand dollars ($1,000); and
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.5.5</span>
                                    &nbsp;Any other good or service specified in any Guidelines.
                                  </p>
                                </li>
                              </ul>
                            </li>
                            <li>
                              <p>
                                <span className="count">3.6.</span>
                                &nbsp;Merchant shall not make any agreement or commitment to any Customer on behalf of Funding Participant without Funding Participant’s prior approval, nor shall Merchant misrepresent any of Funding Participant’s loan terms or requirements to a Customer or Borrower.  Merchant shall make no misrepresentations to Customers regarding loan approval or denial, interest rates, fees, or other terms and conditions associated with the Loan Agreement or Funding Participant’s underwriting or approval process. Merchant has no authority to bind or obligate Funding Participant except as Funding Participant shall expressly authorize. Merchant will transmit to Funding Participant information received from an applicant in a timely and accurate manner, and Merchant will inform an applicant in an accurate and timely manner of Funding Participant’s requests for information and other requirements of such applicant.
                              </p>
                            </li>
                            <li>
                              <p>
                                <span className="count">3.7.</span>
                                &nbsp;Merchant shall make available to its Customers Loan Applications for loans offered by Funding Participant(s).  Merchant’s promotion services will include, but are not limited to, (i) educating the Customer about the ability to finance the purchase of goods and services, (ii) the loan terms, including promotional terms, if available, and (iii) the Loan Application process.
                              </p>
                            </li>
                            <li>
                              <p>
                                <span className="count">3.8.</span>
                                &nbsp;Upon the request of a Customer, Merchant will provide access to Funding Participant’s Loan Application through the Merchant Platform and assist Customer in completing the Loan Application and all other necessary forms to the extent that assistance may be required.
                              </p>
                              <ul>
                                <li>
                                  <p>
                                    <span className="count">3.8.1.</span>
                                    &nbsp;Merchant shall not encourage Customers to supply false, incomplete or misleading information during the Loan Application process. Merchant shall not assist Customers with completing their signature, whether electronic or actual, as part of the Loan Application or Loan Agreement.  Merchant shall assure Program Administrator and Funding Participant that the Customer, and no other person, completed the Customer’s signature on the Loan Application and/or Loan Agreement.
                                  </p>
                                </li>
                                <li>
                                  <p>
                                    <span className="count">3.8.2.</span>
                                    &nbsp;Except for Customers who submit Acceptable Identification directly to Program Administrator, prior to submitting a Loan Application for a Customer, Merchant must collect and retain for a period of five (5) years a copy of at least one (1) form of Acceptable Identification for such Customer in order to verify the identity of such Customer, and Merchant must examine the Acceptable Identification to verify the identity of the Customer.  Merchant must verify that the identity and residence information supplied by Customer to Merchant in relation to Merchant’s sale or provision of goods and/or services to Customer is the same identity and residence information that Customer supplies to Funding Participant in his/her Loan Application and/or Loan Agreement.  If the Merchant is unable to collect Acceptable Identification from a Customer or cannot verify the identity of such Customer based on the Acceptable Identification, the Customer’s Loan Application will be considered incomplete unless and until such time as the Customer’s identity is verified based on Acceptable Identification collected by Merchant.
                                  </p>
                                </li>
                              </ul>
                            </li>
                            <li>
                              <p>
                                <span className="count">3.9.</span>
                                &nbsp;In the event that Program Administrator or a Funding Participant believes Merchant has failed to comply with this Agreement, Program Administrator may suspend Merchant’s participation in the Program and take such other action as it deems appropriate, including terminating this Agreement.
                              </p>
                            </li>
                            <li>
                              <p>
                                <span className="count">3.10.</span>
                                &nbsp;Merchant agrees to follow all of Program Administrator’s written instructions relating to the offering of the Program pursuant to this Agreement.  Program Administrator may change the instructions from time to time upon advance notice to Merchant.
                              </p>
                            </li>
                          </ul>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">4.</span>
                              &nbsp;
                              <b>Merchant Platform Access and Use.</b>
                            </p>

                            <ul>
                              <li>
                                <p>
                                  <span className="count">4.1.</span>
                                  &nbsp;Program Administrator operates the Merchant Platform through its website,
                                  {' '}
                                  <a href="https://www.lendingusa.com"><u>www.lendingusa.com</u></a>
                                  , or other website(s) as may be in effect from time to time.  The Merchant Platform manages the collection of Loan Applications, pre-screening, validation, and e-signing of Loan Agreements for Funding Participants interested in extending consumer credit to finance Customers’ purchase of eligible goods and services offered by Merchant.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.2.</span>
                                  &nbsp;During the term and subject to the terms and conditions of this Agreement, Program Administrator hereby grants Merchant a limited, revocable, non-exclusive, non-transferable license to access and integrate the Merchant Platform solely for the purpose of assisting Customers to apply for financing to facilitate the purchase of eligible goods or services offered and sold by Merchant.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.3.</span>
                                  &nbsp;Merchant shall be provided with a hyperlink to the Merchant Platform access page and one or more unique identification name(s) and associated password(s) (a “
                                  <u>Unique ID</u>
                                  ”) for use in accessing the Merchant Platform. Merchant shall designate employees authorized to use the Merchant Platform (each, an “
                                  <u>Authorized User</u>
                                  ”).  Each Authorized User will be permitted to access the Merchant Platform using Merchant’s Unique ID(s).  Upon periodic request by Program Administrator, Merchant shall identify each Authorized User that has access to Merchant’s Unique ID.  If any Authorized User leaves the employ of Merchant, or is otherwise removed as an Authorized User, Merchant shall immediately change all passwords that were provided to or used by the removed Authorized User.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.4.</span>
                                  &nbsp;Merchant shall not disclose its Unique ID(s) to any individual that is not an Authorized User except to the extent required to enable use of the Merchant Platform in accordance with this Agreement. If Merchant becomes aware of any access to the Merchant Platform through its Unique ID(s) by anyone who is not an Authorized User, Merchant shall immediately cease, and cause its Authorized Users to cease, using the Merchant Platform and shall notify Program Administrator of such unauthorized use.  Further access to the Merchant Platform by Merchant and its Authorized Users shall recommence only upon the express written consent of Program Administrator.  Merchant shall be entirely responsible for any loss or damage that results from the unauthorized use of the Merchant’s Unique ID.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.5.</span>
                                  &nbsp;Merchant shall only connect to the Merchant Platform through the hyperlink provided by Program Administrator (the “
                                  <u>Merchant Link</u>
                                  ”).  Merchant shall use the Merchant Link only in accordance with all applicable security and other policies and procedures which Program Administrator implements and may change from time to time that are provided or communicated to Merchant by Program Administrator.  Notwithstanding the preceding sentence, Merchant may direct a Customer to Program Administrator’s website, which provides a mechanism for a Customer to submit a Loan Application without the assistance of the Merchant (the “
                                  <u>Consumer Link</u>
                                  ”).  In such case, Merchant shall make no representations regarding the Consumer Link other than to inform the Customer of the Consumer Link as an alternative to the Merchant Link.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.6.</span>
                                  &nbsp;Merchant shall use its best efforts to prevent all security breaches through the Merchant Link by implementing stringent security procedures including, without limitation, accessing the Program Administrator systems through password protected systems, changing passwords on a regular basis (and no less frequently than every thirty [30] days), and never accessing the Merchant Platform from a shared or public computer, device or network, and shall act immediately to address any unapproved methods of connection, security breach, or exposure to hacking attempts.  The right to connect to the Merchant Platform may be revoked at any time.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.7.</span>
                                  &nbsp;Merchant shall ensure that no viruses, Trojan horses, disabling code, malware or similar hostile items are coded or introduced into the Merchant Platform. Merchant agrees that, in the event a hostile item is found to have been introduced by Merchant, Merchant shall use commercially reasonable efforts to assist Program Administrator in every possible way (at Merchant’s sole expense if such introduction is due to a breach of its obligations under this Section) in eliminating the hostile item, mitigating its effects and restoring any affected systems on the Merchant Platform to their prior state.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.8.</span>
                                  &nbsp;Any and all information entered into the Merchant Platform by or on behalf of a Customer (“
                                  <u>Customer Data</u>
                                  ”) shall be collected and submitted only with the consent of the Customer.  Without limitation, Customer Data includes data that may be personal and confidential to each Customer, and includes information that may or may not be defined in federal and state privacy statutes as personally identifiable information or sensitive personal information.  Customer Data shall be authenticated by Merchant and submitted with the authorization, pursuant to electronic signature of the Customer, where appropriate.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.9.</span>
                                  &nbsp;Program Administrator or the applicable Funding Participant is the sole and exclusive owner of Customer Data, subject to compliance with applicable laws governing the protection of privacy of such data. Accordingly, Merchant disclaims any interest in Customer Data.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.10.</span>
                                  &nbsp;Merchant and its Authorized Users may have access to Customer Data during the course of a purchase transaction, including during the time that Merchant, its Authorized Users, or a Customer enters data into the Merchant Platform.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">4.11.</span>
                                  &nbsp;Except for entering data into the Merchant Platform, neither Merchant nor any Authorized User shall otherwise retain or record Customer Data, other than as necessary for facilitation of the purchase made by Customers and as necessary to verify the identity of the Customer as required by
                                  &nbsp;
                                  <u>Section 6.6</u>
                                  &nbsp;
                                  of this Agreement (“
                                  <u>Merchant Maintained Data</u>
                                  ”).  Merchant shall be the owner of Merchant Maintained Data and as such, is obligated to protect Merchant Maintained Data pursuant to common law, and state and federal statutes and regulations.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">5.</span>
                              &nbsp;
                              <b>Promotion of Program.</b>
                            </p>

                            <ul>
                              <li>
                                <p>
                                  <span className="count">5.1.</span>
                                  &nbsp;If Merchant chooses to participate in the Program, Merchant agrees to promote the Program in a commercially reasonable manner and in full compliance with all applicable laws and regulations. Any written material referring or relating to the Program, including the fact that Merchant participates in the Program or specific Loan credit terms or credit products Merchant accepts, shall be prepared or furnished by Program Administrator or, if prepared by Merchant, shall be subject to review and approval by Program Administrator in advance of being used by Merchant.  Any such review and approval shall be limited to the review and approval of Program-specific representations and statements, and shall not be construed as a review or approval of any advertising or solicitation materials for any other purpose or for compliance with any other provisions of any local, state or federal laws not related to the Program.
                                </p>
                              </li>

                              <li>
                                <p>
                                  <span className="count">5.2.</span>
                                  &nbsp;Merchant shall not require, through a surcharge or an increase in price or otherwise, any Borrower to pay any fees as a consequence of Borrower applying for or using his/her Loan to pay Merchant. Specifically, Merchant may not charge the Borrower any part of any charge or fee imposed by the Program on Merchant, provided that Merchant may treat such fees as overhead to be distributed across all customers regardless of whether the customer uses a Loan to pay for his/her purchase from Merchant.
                                </p>
                              </li>

                              <li>
                                <p>
                                  <span className="count">5.3.</span>
                                  &nbsp;Merchant shall not use a Loan as a “bridge” loan or intend for the Borrower to use the proceeds of another loan to pay off a Loan under the Program.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <br />

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">6.</span>
                              &nbsp;
                              <b>Loan Term, Approval, and Loan Applications.</b>
                            </p>

                            <ul>
                              <li>
                                <p>
                                  <span className="count">6.1.</span>
                                  &nbsp;Through the Program, a Funding Participant may offer Loan(s) to Merchant’s qualified Customers under a separate Loan Agreement between Funding Participant and such qualified Customer.  As between the Parties, Program Administrator (i) has sole authority to prescribe the terms and conditions of the Loan Application, the Loan Agreement and each Loan (including interest rate, maximum amount and term), (ii) may prospectively modify such terms and conditions with respect to Loans for which approval is granted subsequent to the time of the modification, (iii) may at any time change the credit standards without notice to Merchant, and (iv) may reject and accept Loan Applications in its sole discretion.  A Funding Participant shall not be obligated to take any action with respect to a Loan, including accepting the Loan Application or making future credit available to a prospective Borrower or a Borrower, and has no obligation to approve any particular Loan or to approve Loans meeting any particular set of requirements.  A Funding Participant may withdraw any previously issued Loan approval prior to funding of the Loan, which Loan approvals, unless earlier withdrawn, shall automatically expire at the end of the purchase window for the applicable credit product.  Funding Participants may at any time suspend and restart any of the Loan products offered in connection with the Program.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">6.2.</span>
                                  &nbsp;Except as provided herein, Funding Participants shall own the Loans and shall bear the credit risk for the Loans.  Merchant acknowledges and agrees that it shall have no ownership interest in the Loans.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">6.3.</span>
                                  &nbsp;Merchant agrees to submit all Loan Applications in accordance with this Agreement and Guidelines.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">6.4.</span>
                                  &nbsp;Merchant agrees to provide its sales and finance employees with necessary training and equipment to submit Loan Applications in accordance with this Agreement and Guidelines.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">6.5.</span>
                                  &nbsp;In addition, Merchant may, with a prospective Borrower’s prior written consent, (i) submit a completed Loan Application to the Program by such means as are set forth in the Guidelines and (ii) receive, and forward the Loan Documents to the prospective Borrower.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">6.6.</span>
                                  &nbsp;Merchant agrees that, with respect to all Loan Applications or Loan Application information forms, it shall provide all information requested and required by this Agreement, including obtaining all signatures, as applicable, on Loan Applications, and ensure all information requested is complete and legible.  In addition, Merchant agrees that it will verify the identification of each individual applying for credit by obtaining Acceptable Identification (or another identification method authorized in any Guidelines).
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">6.7.</span>
                                  &nbsp;Merchant agrees that it will not sign Loan Applications or Loan Documents on behalf of its Customers.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">7.</span>
                              &nbsp;
                              <b>
                                Payments for Goods and Services and Transmission of Funds.
                              </b>
                            </p>
                          </li>
                          <li>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">7.1.</span>
                                  &nbsp;Merchant acknowledges that payment for goods or services purchased by Customers who enter a Loan Agreement shall be made directly to Merchant by Funding Participant and not by Program Administrator.  Merchant further acknowledges that Funding Participant shall be the creditor at origination.  All transactions financed pursuant to this Agreement shall be for personal, family or household purposes unless otherwise authorized in writing by Program Administrator.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">7.2.</span>
                                  &nbsp;Merchant agrees that Program Administrator or the Funding Participants has the sole right to receive payments on Loans.  Merchant agrees not to attempt to collect on a Loan unless specifically authorized in writing by the Funding Participant, directly or indirectly by Program Administrator.  Merchant agrees to hold in trust for the Funding Participant any payment received by Merchant in respect of such Loan and to deliver such payment to the Funding Participant, together with the Borrower’s name, account number, and any correspondence accompanying the payment, within five (5) days of receipt by Merchant.  Merchant agrees that Merchant shall be deemed to have endorsed, in favor of the Funding Participant to which any such payment relates, any Borrower payments by check, money order or other instrument made payable to Merchant that a Borrower presents to Program Administrator, and Merchant hereby authorizes the Funding Participant (or, if designated by such Funding Participant, Program Administrator on its behalf) to supply such necessary endorsements on behalf of Merchant.  Merchant agrees that it shall not, directly or indirectly, make any payment on a Loan on behalf of a Borrower without the prior written approval of Program Administrator, acting on behalf of Funding Participant, and Program Administrator, in its sole discretion, shall have the right to immediately (i) suspend Merchant’s right to submit new funding transactions on approved Loans or submit new Loan Applications or (ii) terminate this Agreement in the event that Merchant fails to comply with this provision.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">8.</span>
                              &nbsp;
                              <b>
                                Fees.
                              </b>
                            </p>
                          </li>
                          <li>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">8.1.</span>
                                  &nbsp;Merchant agrees that the price being charged to a Customer for the purchase of goods and/or services as evidenced by the Loan Agreement (the “
                                  <u>Financed Price</u>
                                  ”) is the same price that would be charged to the Customer if the Customer paid cash for the goods and/or services.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">8.2.</span>
                                  &nbsp;Merchant agrees that a Funding Participant’s offer to extend consumer financing or enter a Loan Agreement may be conditioned upon (among other things) Merchant accepting as payment in full a specified percentage (the “
                                  <u>Funding Percentage</u>
                                  ”) of the Deposit, if applicable, and the Financed Price of the goods or services to be delivered by Merchant (“
                                  <u>Payout Amount</u>
                                  ”).  Merchant agrees that the Payout Amount shall equal the sum of any applicable Deposit and the Financed Price multiplied by the Funding Percentage.  The terms and conditions of a Funding Participant’s offer to extend consumer financing, including any applicable Funding Percentage and merchant discount fee (the “
                                  <u>Merchant Discount Fee</u>
                                  ”), shall be set forth on a webpage accessible through the Merchant Platform, and at all times are contingent on the Funding Participant and Customer entering into a Loan under the Program.  The amounts vary and may be amended from time to time.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">8.3.</span>
                                  &nbsp;Merchant acknowledges and agrees that, upon Merchant accepting the Payout Amount as payment in full, neither Program Administrator, the Funding Participant nor the Customer shall have any liability to Merchant for any sums other than the Payout Amount. In the event of noncompliance with the terms of this
                                  &nbsp;
                                  <u>Section 8</u>
                                  , Funding Participant may refuse to remit Loan Proceeds (as defined below) or require Merchant to timely reimburse previously remitted Loan Proceeds, or the disputed portion thereof, as applicable.  A failure to timely provide reimbursement under this
                                  &nbsp;
                                  <u>Section 8.3</u>
                                  &nbsp;
                                  may result in a Chargeback, pursuant to
                                  &nbsp;
                                  <u>Section 10.4</u>
                                  &nbsp;
                                  of this Agreement.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">8.4.</span>
                                  &nbsp;Merchant agrees not to disclose to any Customer the Funding Percentage or Payout Amount, the proposed or actual amount of Loan Proceeds paid by Funding Participants to Merchant, or any difference between the Payout Amount or amount of Loan Proceeds and the Financed Price.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">8.5.</span>
                                  &nbsp;Merchant agrees not to impose the Merchant Discount Fee on any Customer either by charging the Customer directly for the Merchant Discount Fee or indirectly by charging a higher cost for its goods or services where the Customer finances the purchase of such goods or services pursuant to the Loan Program.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">8.6.</span>
                                  &nbsp;Merchant shall not charge to or receive from a Customer any fee, charge or other compensation of any kind in connection with any Loan Application or Loan Agreement.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">9.</span>
                              &nbsp;
                              <b>Merchant Settlement Account, Authorization for Automatic Deposits (ACH Credits) and Direct Debits (ACH Debits).</b>
                            </p>
                          </li>
                          <li>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">9.1.</span>
                                  &nbsp;Before Funding Participants receive or otherwise processes a Loan Application, each Customer must have agreed in the Loan Application that, if Funding Participant approves the Loan Application, Funding Participant will deliver the proceeds payable to Merchant by Funding Participant under the Loan Agreement (“
                                  <u>Loan Proceeds</u>
                                  ”) directly to Merchant via the National Automated Clearing House Association (“
                                  <u>ACH Network</u>
                                  ”) to an account designated to Program Administrator, on behalf of itself and Funding Participants (as applicable), by Merchant (“
                                  <u>Settlement Account</u>
                                  ”).  Merchant shall be solely liable for all fees and costs associated with the Settlement Account.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">9.2.</span>
                                  &nbsp;Provided that the Settlement Account designated by Merchant in the Application is verified to the satisfaction of Program Administrator, the Settlement Account shall be used to receive any Loan Proceeds, and for debiting Chargebacks, as provided by and in accordance with this Agreement.  Merchant authorizes Program Administrator and Funding Participant, including any Sponsor (as applicable), to initiate credit entries for amounts that Program Administrator or Funding Participant may owe Merchant, or to disburse any Loan Proceeds or other sums that may otherwise be due to Merchant under this Agreement.  Merchant authorizes Program Administrator and Funding Participant to initiate debit entries for any credit entries in error or the amount which Merchant owes under this Agreement, which is more than the amount owed Merchant.  Such credit and debit entries will be to the bank account identified by Merchant.  Merchant and Program Administrator acknowledge that the origination of ACH transactions described in this
                                  &nbsp;
                                  <u>Section 9</u>
                                  &nbsp;
                                  must comply with applicable law and NACHA rules.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">9.3.</span>
                                  &nbsp;The authorizations set forth in this
                                  &nbsp;
                                  <u>Section 9</u>
                                  &nbsp;
                                  will remain in effect until the date on which no Loans remain outstanding and all Chargebacks and any other authorized debits have been processed, provided that in the event of termination of this Agreement, Merchant agrees to maintain the Settlement Account with sufficient funds until such time as Merchant on the one hand, and together Program Administrator and Funding Participant on the other, agree that all Chargebacks and other adjustments are processed.  Merchant shall not close the Settlement Account without first informing Program Administrator, the Funding Participant, or their designated Sponsor, as applicable, five (5) Business Days prior to the date of the closing of the Settlement Account and providing Program Administrator information for another bank account (the “
                                  <u>Substitute Settlement Account</u>
                                  ”), which shall be used for the same purposes as the prior Settlement Account. Merchant shall not close a Settlement Account or Substitute Settlement Account until Program Administrator has confirmed that the Substitute Settlement Account is verified to Program Administrator’s satisfaction and that there are sufficient funds in the Substitute Settlement Account to cover pending and projected Chargebacks, or any other amounts owed under this Agreement.  Merchant’s obligations under this
                                  &nbsp;
                                  <u>Section 9.3</u>
                                  &nbsp;
                                  shall survive termination of this Agreement.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">9.4.</span>
                                  &nbsp;Neither Program Administrator nor Funding Participant shall be liable for any delays in the transmission of Loan Proceeds or errors in Settlement Account entries caused by third parties, including, but not limited to, a clearinghouse, Merchant’s financial institution, or any agent of Merchant.  Program Administrator agrees to comply with written notifications from Merchant that alter Merchant’s bank account information (i.e., name and address of the bank or financial institution, transit/routing number or account number), provided that Program Administrator receives such notification in sufficient time and manner to give Program Administrator and the bank or financial institution reasonable opportunity to act on it.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">9.5.</span>
                                  &nbsp;Merchant agrees to be bound by the terms of the operating rules of the ACH Network, as in effect from time to time.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">9.6.</span>
                                  &nbsp;In the event Merchant fails to verify that the price charged for the relevant goods or services is the same as the Financed Price specified in the Customer’s Loan Agreement, as disclosed by Funding Participant on the final confirmation fax or other electronic notification of acceptance of the Loan Agreement prior to Funding Participant’s transmission of Loan Proceeds, Merchant is responsible for the difference and shall either: (i) if  Funding Participant remits Loan Proceeds to Merchant in an amount that is greater than the actual cash purchase price offered by Merchant to its Customer, then Merchant shall remit the difference to Funding Participant via wire transfer in accordance with the procedures specified herein, so that Funding Participant may apply such amount to the Customer’s Loan account; or (ii) if Funding Participant remits Loan Proceeds in an amount that is less than the sale, then Merchant is solely responsible for the difference.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">10.</span>
                              &nbsp;
                              <b>Returns and Chargebacks.</b>
                            </p>
                          </li>
                          <li>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">10.1.</span>
                                  &nbsp;
                                  <b>Returns.</b>
                                </p>
                                <ul>
                                  <li>
                                    <p>
                                      <span className="count">10.1.1.</span>
                                      &nbsp;Merchant shall immediately notify Program Administrator (
                                      <u>RMteam@lendingusa.com</u>
                                      ) if any Customer who has entered a Loan Agreement returns or rejects any goods or services financed by the Loan Agreement by: (i) terminating an agreement with Merchant to purchase such goods prior to delivery or such services prior to performance; (ii) rejecting the delivery of such goods or the performance of such services; or (iii) returning such goods or disputing the performance of such services in a manner consistent with Merchant’s relevant policy or applicable law.  Merchant’s notification shall include the Customer’s name, account number, contract or purchase date, Financed Price, amount of Loan Proceeds received by Merchant, and portion thereof corresponding to the rejected or returned goods or services.
                                    </p>
                                    <ul>
                                      <li>
                                        <p>
                                          <span className="count">10.1.1.1.</span>
                                          &nbsp;If any Customer who has entered a Loan Agreement returns or rejects any goods or services financed under the Loan Agreement, Merchant shall promptly reimburse the amount of any Loan Proceeds transferred to the Merchant, or any portion thereof, related to the goods or services returned or rejected, as applicable.
                                        </p>
                                      </li>
                                      <li>
                                        <p>
                                          <span className="count">10.1.1.2.</span>
                                          &nbsp;Any reimbursement for returned or rejected goods or services shall be remitted via wire transfer to Funding Participant or Program Administrator, as applicable, no later than fifteen (15) days after the date the goods or services were returned or rejected.  A failure to timely provide reimbursement for returned or rejected goods or services may result in a Chargeback, pursuant to pursuant to
                                          &nbsp;
                                          <u>Section 10.4</u>
                                          &nbsp;
                                          of this Agreement.
                                        </p>
                                      </li>
                                      <li>
                                        <p>
                                          <span className="count">10.1.1.3.</span>
                                          &nbsp;Upon receipt of such reimbursement for returned or rejected goods, (a) in the case of a rejection or return of all goods or services, Funding Participant shall waive any accrued financing charges or other charges owed by the Customer and shall cancel the Loan Agreement, and (b) in the case of a rejection or return of only a portion of the goods or services, Funding Participant shall apply the reimbursed funds to the outstanding amounts owed on the Loan (which may include accrued but unpaid interest or fees), as provided for in the Loan Agreement, and the Customer will continue to be obligated to pay the remaining loan balance and interest in accordance with the terms of the Loan Agreement.
                                        </p>
                                      </li>
                                    </ul>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.1.2.</span>
                                      &nbsp;Notwithstanding the provisions of
                                      &nbsp;
                                      <u>Section 10.1</u>
                                      , Merchant may be authorized, pursuant to applicable law and the terms of the applicable agreement between the Merchant and the Customer, to charge the Customer a fee for cancellation of the provision of the goods or services (“
                                      <u>Cancellation Fee</u>
                                      ”).  Notwithstanding the foregoing, Program Administrator or Funding Participant may, at its discretion, require Merchant to reimburse the amount of any Loan Proceeds transferred to the Merchant, or any portion thereof, related to the Cancellation Fee or the canceled goods or services.
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.1.3.</span>
                                      &nbsp;The required return notification described in
                                      &nbsp;
                                      <u>Section 10.1.1</u>
                                      &nbsp;
                                      must be received within one hundred twenty (120) days of the subject Loan’s origination date, otherwise the transaction is considered final and this
                                      &nbsp;
                                      <u>Section 10.1</u>
                                      &nbsp;
                                      will not apply.
                                    </p>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.2.</span>
                                  &nbsp;
                                  <b>Disputes.</b>
                                </p>
                                <ul>
                                  <li>
                                    <p>
                                      <span className="count">10.2.1.</span>
                                      &nbsp;Merchant will deliver the contracted or purchased goods or services to its Customer in a timely manner and as agreed upon between Merchant and its Customer.
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.2.2.</span>
                                      &nbsp;Merchant shall immediately notify Program Administrator if any Customer who has entered a Loan Agreement demands a refund for or otherwise raises a billing or other dispute in connection with such goods or such services, the execution of the transaction authorization, the Loan Application or Loan Agreement, or the goods and services purchased (each, a “
                                      <u>Dispute</u>
                                      ”), and Program Administrator shall immediately notify Merchant if Program Administrator receives a Dispute from a Customer who has entered a Loan Agreement. Some examples of Disputes include disputes over the delivery, quality or performance of the Merchant’s goods or services; product warranties; the sale of goods or services not having been authorized by the Customer; or credit adjustments issued by the Merchant but not posted to the Loan due to Merchant’s failure to submit the adjustment to the Program Administrator via the Program.
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.2.3.</span>
                                      &nbsp;Merchant shall timely investigate and make a good faith effort to resolve each Dispute that it receives from a Customer or Program Administrator.  If a Customer claims that s/he did not receive the contracted or purchased goods or services, disputes the nature, quality, merchantability or suitability thereof, or otherwise disputes his/her payment obligations in relation thereto, Merchant will promptly provide proof of receipt, quality, merchantability and suitability or attempt to remedy the situation through exchange, additional performance or other commercially reasonable substitution or correction as applicable.
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.2.4.</span>
                                      &nbsp;No later than fifteen (15) Business Days after the date Merchant receives or is notified of a Dispute, Merchant shall notify Program Administrator in writing of the resolution thereof or the action Merchant will take to resolve such Dispute. Merchant shall provide Program Administrator with all such information and documentation as Program Administrator may reasonably request in connection with the Dispute or resolution thereof.
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.2.5.</span>
                                      &nbsp;If the Dispute is not promptly resolved within fifteen (15) Business Days as specified in
                                      &nbsp;
                                      <u>Section 10.2.4</u>
                                      , Merchant agrees to return all or the relevant portion of the Loan Proceeds via wire transfer to Funding Participant within two (2) Business Days of receiving notification from Program Administrator of Merchant’s requirement to refund the designated amount of Loan Proceeds to Program Administrator. A failure to timely provide reimbursement for an unresolved Dispute may result in a Chargeback pursuant to
                                      &nbsp;
                                      <u>Section 10.4</u>
                                      &nbsp;
                                      of this Agreement.
                                    </p>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.3.</span>
                                  &nbsp;
                                  <b>Fraudulent Conduct.</b>
                                </p>
                                <ul>
                                  <li>
                                    <p>
                                      <span className="count">10.3.1.</span>
                                      &nbsp;Neither Funding Participant nor Program Administrator shall be liable for any loss resulting from any fraud committed by Merchant or any Customer in relation to the origination of the Loan Agreement, or if information supplied by Merchant or the Customer is false, inaccurate or unverifiable, including information or documentation regarding the identity of the Customer (collectively, “
                                      <u>Fraudulent Conduct</u>
                                      ”).
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.3.2.</span>
                                      &nbsp;If Merchant provides any goods or services which are to be financed pursuant to a Loan Agreement and the transaction is determined to involve Fraudulent Conduct, Merchant is liable for any loss resulting from such transaction and shall not seek payment for such goods or services from Funding Participant or Program Administrator.
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.3.3.</span>
                                      &nbsp;If, after Loan Proceeds have been transferred to Merchant, a transaction is determined to involve Fraudulent Conduct, Merchant shall promptly reimburse to Funding Participant or Program Administrator, as applicable, the full amount of the Loan Proceeds transferred to Merchant, and shall otherwise comply with any further demands for reimbursement made by Funding Participant or Program Administrator, as applicable.
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.3.4.</span>
                                      &nbsp;Any reimbursement for Fraudulent Conduct shall be remitted to the Funding Participant or Program Administrator, as applicable, no later than fifteen (15) days after the date the Merchant was notified of the Fraudulent Conduct. A failure to timely provide reimbursement for Fraudulent Conduct may result in a Chargeback pursuant to
                                      &nbsp;
                                      <u>Section 10.4</u>
                                      &nbsp;
                                      of this Agreement.
                                    </p>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.4.</span>
                                  &nbsp;
                                  <b>Chargebacks.</b>
                                </p>
                                <ul>
                                  <li>
                                    <p>
                                      <span className="count">10.4.1.</span>
                                      &nbsp;Without duplication of any amounts paid by Merchant pursuant to
                                      &nbsp;
                                      <u>Section 17</u>
                                      , Merchant agrees that it will refund on demand, and Program Administrator or the Funding Participants may (i) charge back against Merchant, the amount of any Loan affected, plus any finance or other charges related to the Loan under the Borrower’s Loan Agreement, (ii) refuse to transfer, or refuse to initiate the transfer of, Loan Proceeds that would otherwise be payable to Merchant under the Loan Agreement, (iii) require Merchant to reimburse Loan Proceeds remitted to Merchant under the Loan Agreement, or the disputed portion thereof, as applicable (plus the amount of any related transfer or transaction fee and any Late Payment Fee [defined below]), (iv) withdraw or debit from the Settlement Account the amount of Loan Proceeds remitted to Merchant under the Loan Agreement, or the disputed portion thereof, as applicable (plus the amount of any related transfer or transaction fee and any Late Payment Fee), or (vi) require Merchant to purchase the relevant Loan(s), made pursuant to this Agreement, accept assignment of the Loan(s), and pay to Program Administrator (or  Funding Participant or its designees or assignees, as applicable), upon demand, an amount of Loan Proceeds remitted to Merchant under the Loan Agreement plus any Late Payment Fee (each such action, a “
                                      <u>Chargeback</u>
                                      ”), in each of the following events:
                                    </p>
                                    <ul>
                                      <li>
                                        <p>
                                          <span className="count">10.4.1.1.</span>
                                          &nbsp;Program Administrator or the Funding Participant determines that Merchant has breached or failed to fulfill any of its obligations under this Agreement, including the Guidelines, or has breached any of its representations or warranties under this Agreement;
                                        </p>
                                      </li>
                                      <li>
                                        <p>
                                          <span className="count">10.4.1.2.</span>
                                          &nbsp;If Borrower asserts any claim or defense against Program Administrator or the Funding Participant as a result of any act or omission of Merchant in violation of any applicable law (other than those that relate solely to the terms and conditions of such Loan); or
                                        </p>
                                      </li>
                                      <li>
                                        <p>
                                          <span className="count">10.4.1.3.</span>
                                          &nbsp;If Program Administrator or the Funding Participant determines that Merchant has not, to the satisfaction of Program Administrator, (i) resolved a Dispute or funding discrepancy within fifteen (15) Business Days after the date of receipt or discovery by Merchant, (ii) timely remitted a reimbursement required pursuant to
                                          &nbsp;
                                          <u>Section 10.2.5</u>
                                          &nbsp;
                                          of this Agreement, (iii) timely remitted a reimbursement for returned or rejected goods or services, (iv) timely remitted a reimbursement for Fraudulent Conduct, or (v) otherwise complied with the terms of this Agreement, Funding Participant or Program Administrator, as applicable.
                                        </p>
                                      </li>
                                    </ul>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.5.</span>
                                  &nbsp;
                                  With respect to any Chargeback or refund:
                                </p>
                                <ul>
                                  <li>
                                    <p>
                                      <span className="count">10.5.1.</span>
                                      &nbsp;Merchant agrees that Funding Participant or Program Administrator, as applicable, is entitled to a fee equal to two percent (2%) of the Loan Proceeds in dispute if the Chargeback occurs thirty-one (31) to sixty (60) days after the goods or services are returned or rejected or the Dispute is received, and equal to an additional one percent (1%) of the Loan Proceeds in dispute for every thirty (30) days thereafter until the Chargeback occurs (“
                                      <u>Late Payment Fee</u>
                                      ”).
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">10.5.2.</span>
                                      &nbsp;If, after a Chargeback is processed, the disputed amount is subsequently paid by the Customer, Funding Participant will remit to Merchant the amount paid by Customer.
                                    </p>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.6.</span>
                                  &nbsp;Funding Participants are not required to disburse funds to Merchant for a Loan Agreement that is being charged back.  If Funding Participant has already disbursed funds to Merchant for such Loan Agreement, the Funding Participants, at their sole discretion, may deduct the amount to be charged back from the Settlement Account or offset such amount from a future payment to Merchant.  Alternatively, the Funding Participants may demand that Merchant pay the Funding Participants the amount of the Chargeback (plus the amount of any related transfer or transaction fee and any Late Payment Fee), and Merchant shall make such payment with three (3) Business Days of such demand.  Any Chargebacks that are not paid as described above shall be due and payable by Merchant promptly on demand.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.7.</span>
                                  &nbsp;In its reasonable discretion but upon prior notice to Merchant, the Funding Participant, as communicated by Program Administrator, may compromise and settle any claim made by any Borrower if such claim may give the Funding Participant a right to Chargeback (or a right to a refund) in accordance with this Agreement.  The Funding Participant, as communicated by Program Administrator, may settle such claim in an amount equal to the amount paid for the disputed good and services, not to exceed the face amount of any sale of good and services purchased by Borrower.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.8.</span>
                                  &nbsp;If Funding Participant exercises its right of Chargeback (or seeks a refund) in accordance with this Agreement, such Funding Participant may set off or recoup amounts charged-back or subject to refund against any sums due to Merchant under this Agreement and, if the amount of such Chargeback or refund exceeds the sums due to Merchant, such Funding Participant may demand payment from Merchant for such amount (or set off or recoup such amount up to the amount of sums due to Merchant and demand payment from Merchant for such excess amount).
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.9.</span>
                                  &nbsp;If the full amount due with respect to any Loan is charged back or otherwise refunded by Merchant, Merchant shall be entitled to recover the unpaid amount of the face amount of any sale of good and services purchased by Borrower from the Customer as if the financing had not occurred, although Merchant shall have no rights under the Loan Agreement or to the proceeds of the Loan.  In such event, Merchant shall bear all liability and risk of loss associated with such Transaction Request without warranty by, or recourse or liability to, Program Administrator or Funding Participant.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">10.10.</span>
                                  &nbsp;
                                  <b>Contest of Compliance with Laws.</b>
                                  &nbsp;In addition to the foregoing return and Chargeback provisions, in the event it is discovered (as a result of notices by a Customer or otherwise) that an executed Loan Agreement would not be in compliance with any applicable laws, rules or regulations, the Funding Participant has the right to deny funding thereof (to the extent not already funded) or to Chargeback from Merchant any proceeds thereof then on deposit in the Settlement Account with respect to the Loan Agreement that is the subject of such inquiry or dispute.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">11.</span>
                              &nbsp;
                              <b>Intellectual Property.</b>
                            </p>
                          </li>
                          <li>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">11.1.</span>
                                  &nbsp;
                                  <b>API License.</b>
                                  &nbsp;During the term and subject to the terms and conditions of this Agreement, Program Administrator hereby grants Merchant a limited, revocable, non-exclusive, non-transferable license to access and integrate Program Administrator’s APIs in order to provide and to enable Merchant to access, use, perform and display (publicly or otherwise) the Program or Merchant Platform.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">11.2.</span>
                                  &nbsp;
                                  <b>Trademark License.</b>
                                  &nbsp;During the term and subject to the terms and conditions of this Agreement, Program Administrator hereby grants Merchant a limited, revocable, non-exclusive, non-transferable license and right to use, reproduce, display, and distribute Program Administrator’s name, logo and any other trademarks, trade names, service marks, photographs, graphics, artwork, text and other content provided or specified by Program Administrator, in any and all media formats, whether registered or unregistered (collectively the “
                                  <u>Marks</u>
                                  ”), solely in connection with and solely to the extent reasonably necessary for the purposes of this Agreement.   Use of the Marks does not create in Merchant’s favor any right, title or interest in the Marks or any continuing rights to market or distribute the Program.  Merchant shall not register or apply for registration of any of Program Administrator’s Marks (or any similar trademarks, service marks or logos) for itself or any other party. Merchant agrees to cooperate with Program Administrator if Program Administrator seeks to proceed with any infringement action regarding such rights.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">11.3.</span>
                                  &nbsp;
                                  <b>Program Administrator Intellectual Property.</b>
                                  &nbsp;Merchant agrees and acknowledges that Program Administrator is the exclusive owner of and retains all right, title, and interest in any and all software, technology or tools used by Program Administrator to promote, market, sell, generate, or distribute the Program, including Program Administrator’s name, logo and any other trademarks or patented or copyrighted material (collectively, “
                                  <u>Program Administrator IP</u>
                                  ”).  Merchant may not, nor may Merchant allow any third party to: (i) modify, translate, reverse engineer, decompile, disassemble, otherwise attempt to derive source code from, or create derivative works based on, the Program Administrator IP; (ii) make unauthorized copies of the Program Administrator IP; (iii) distribute or market the Program and any Program Administrator IP, except to Customers, without Program Administrator’s prior written authorization; (iv) remove any proprietary notices, labels or Marks on or in any copy of the Program or Program Administrator IP; or (v) use the Program and Program Administrator IP in any manner or for any purpose other than for which the Program and Program Administrator IP have been incorporated or for which the Program and Program Administrator IP have been provided.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">11.4.</span>
                                  &nbsp;
                                  <b>Notification Required.</b>
                                  &nbsp;Merchant shall notify Program Administrator and Funding Participant immediately upon receiving a cease and desist or similar letter with regard to any copyright, trademark or patent infringement related to or arising from a website or e-mail that includes any information regarding, or hyperlink to, Program Administrator’s or Funding Participant’s websites, products or services.  Such notification shall include a copy of the cease and desist letter received.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">11.5.</span>
                                  &nbsp;
                                  <b>Restrictions on Use of Program.</b>
                                  &nbsp;Merchant shall not: (i) offer for sale or lease, sell, resell, lease or in any way transfer the Program; (ii) attempt to create a substitute or similar service through use of, or access to, the Program; or (iii) perform any benchmark tests against a competing provider without the prior written consent of Program Administrator (any results of such permitted benchmark testing shall be deemed Confidential Information of Program Administrator and, other than as provided in
                                  &nbsp;
                                  <u>Section 13</u>
                                  , shall not be shared with any other party without Program Administrator’s prior written consent).  Merchant will ensure that Customers and third parties do not access or use the Program in a way intended to avoid incurring fees, misrepresent usage or performance data, misrepresent transaction amount or item data, or misrepresent user information.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">12.</span>
                              &nbsp;
                              <b>Representations and Warranties.</b>
                              &nbsp;Merchant represents, warrants and covenants as of the day it enters into this Agreement and as of the date of each submission of a Loan Application and execution of a Loan Agreement that:
                            </p>
                          </li>
                          <li>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">12.1.</span>
                                  &nbsp;
                                  <b>Due Organization and Good Standing.</b>
                                  &nbsp;Merchant is duly formed, validly existing, and in good standing under the laws of its state of incorporation or formation.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.2.</span>
                                  &nbsp;
                                  <b>Duly Licensed.</b>
                                  &nbsp;Merchant and each of its employees, as well as any agents, independent contractors or service providers, is duly qualified or licensed to do business and is in good standing (where such concept is recognized under applicable law) in each jurisdiction where the nature of its business or the ownership or operation of its properties makes such qualification or licensing necessary.  Merchant and each of its employees, as well as any agents, independent contractors or service providers, have and will maintain any licenses, permits, approvals, registrations, certifications and the like that are required by applicable law to conduct its business, to deliver its products and services, to engage in the provision of goods or services offered and sold by Merchant, to participate in the Program and to perform its obligations under this Agreement, and are in good standing with, and not subject to any disciplinary actions by, any authority that issues such licenses, permits, approvals, registrations, certifications or the like.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.3.</span>
                                  &nbsp;
                                  <b>Authority and Capacity.</b>
                                  &nbsp;Merchant has all requisite right, power and authority to execute and deliver, and perform its obligations under, this Agreement and to consummate the transactions contemplated hereby.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.4.</span>
                                  &nbsp;
                                  <b>Origination of Loans and Loan Documents.</b>
                                  &nbsp;As to each Loan Application, Transaction Request, or other material presented or delivered in connection with the Program, and the transaction it evidences, Merchant represents and warrants the following:
                                </p>
                                <ul>
                                  <li>
                                    <p>
                                      <span className="count">12.4.1.</span>
                                      &nbsp;that Merchant has verified the identity of the Customer and that the Customer was of legal age and competent to execute the Loan Application, Loan Agreement and transaction authorization at the time of the execution thereof;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.2.</span>
                                      &nbsp;that the Loan Application, Loan Agreement and Transaction Request are bona fide and were actually made and agreed to by each person identified as an applicant or Borrower;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.3.</span>
                                      &nbsp;the Loan Application accurately depicts the information provided by the Customer.  Any statements by Merchant that the identity, income, or delivery of the product to the Customer were verified are accurate in all material respects;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.4.</span>
                                      &nbsp;that each Loan Application, Loan Agreement and Transaction Request (i) will not involve the use of the Loan Proceeds for any purpose other than for the purchase of the products and services purchased from Merchant that are the subject of the Loan Agreement and underlying transaction, which are truly and accurately described therein, (ii) involves products and services that are fit and merchantable for their intended purpose, that such products and services have been delivered into the possession of the Borrower and any services so described have been performed, and that all installation, if applicable, has been completed in a proper and workmanlike manner to the Borrower’s complete satisfaction, and (iii) represents Merchant’s satisfactory performance of all of its other obligations to the Borrower in connection with the transaction evidenced by such Transaction Request;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.5.</span>
                                      &nbsp;that Merchant has conveyed full and complete title to the products and services sold, if any, to the Borrower;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.6.</span>
                                      &nbsp;that such transaction is, in all respects, in compliance with the Guidelines, this Agreement, and all laws, rules and regulations of any federal, state or local governmental agency governing the same;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.7.</span>
                                      &nbsp;that Merchant has no knowledge or notice of any fact, event, issue or circumstance that would impair enforceability or collection of the Loan as against Borrower;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.8.</span>
                                      &nbsp;that there are no liens, mortgages, encumbrances or security interests upon the Transaction Request or the rights evidenced by the Transaction Request;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.9.</span>
                                      &nbsp;that there are no present or future unvested or unrecorded rights related to such transaction that could give rise to a mechanic’s, materialman’s or laborer’s lien, except to the extent those rights are in favor of Merchant, in which event Merchant agrees not to assert those rights to the detriment of Funding Participant;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.10.</span>
                                      &nbsp;that Borrower has no claim or defense to payment of any amount reflected on such Transaction Request based upon materials or workmanship or any act or omission of Merchant or Merchant’s employees, contractors, laborers or representatives;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.11.</span>
                                      &nbsp;that Merchant has made no representations or warranties to Borrower other than warranties approved by the Funding Participant, as communicated by Program Administrator, or a third party’s or manufacturer’s standard warranties, and in the event a manufacturer or third party breaches a standard warranty, Merchant will cure such breach within thirty (30) days of notice thereof;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.12.</span>
                                      &nbsp;that Merchant does not engage in discriminatory practices in the manner in which it offers Program or assists Customers in completing Loan Applications which could result in claims of violation of the Equal Credit Opportunity Act, Regulation B or its state law counterparts;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.13.</span>
                                      &nbsp;that Merchant has not increased the purchase price or added any additional fees as a result of Borrower’s use of the Loan to purchase products and services from Merchant;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.14.</span>
                                      &nbsp;that Merchant has not taken any adverse action against a prospective Borrower or Borrower because the prospective Borrower or Borrower is a member of a protected class, as defined by applicable law, or because the prospective Borrower or Borrower has chosen to use credit to finance the purchase, nor has Merchant engaged in any practice that has or could have an impermissible negative or disparate impact on members of any protected class, including steering prospective Borrowers or Borrowers to more expensive or less favorable financing options because of the prospective Borrowers’ or Borrowers’ membership in a protected class; and
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">12.4.15.</span>
                                      &nbsp;the loans are offered pursuant to Funding Participant’s established standards as provided to Merchant by Program Administrator from time to time.
                                    </p>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.5.</span>
                                  &nbsp;
                                  <b>Compliance with Applicable Laws.</b>
                                  &nbsp;Merchant is in compliance with, and will continue to comply with, all Program Administrator policies and procedures, this Agreement, including any addendum, Guidelines, and all applicable federal, state, and local laws, ordinances and regulations.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.6.</span>
                                  &nbsp;
                                  <b>No Fraud.</b>
                                  &nbsp;Neither Merchant nor any of its agents or employees shall in any manner have been guilty of any civil or criminal fraud with respect to the creation of any sale of products or services or the funding of a Loan by Funding Participant and/or Program Administrator.  No Customer was induced to enter into any of the Loan Application by any false or misleading statements, including those relating to privacy and data security and to its sale of products and services, point-of-sale practices and representations made by Merchant’s employees and representatives.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.7.</span>
                                  &nbsp;
                                  <b>No Pending Litigation; Government Proceedings.</b>
                                  &nbsp;To the best of Merchant’s knowledge, (i) there is no pending or threatened civil, criminal or administrative action, suit, demand, claim, hearing, proceeding or investigation against or relating to Merchant or any of its Affiliates, (ii)there is no litigation, proceeding or governmental investigation existing or pending or threatened, (iii) there is no order, injunction or decree outstanding against or relating to Merchant or its Affiliates, that has not been disclosed by Merchant to Program Administrator or its counsel in writing prior to the execution of this Agreement which could have a material adverse effect upon the Loans or any of Merchant’s agreements or obligations hereunder, nor does Merchant know of any basis for any such litigation, proceeding, or governmental investigation. Merchant has not violated any Applicable Law which may materially affect any of the contracts with Customers or any of Merchant’s agreements or obligations hereunder.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.8.</span>
                                  &nbsp;
                                  <b>Disclosure of Consent Order; Proceedings.</b>
                                  &nbsp;Merchant has disclosed to Program Administrator in writing, prior to executing this Agreement, and agrees to promptly disclose in writing at any time after executing this Agreement, the existence of any past federal or state decrees, orders, or consent agreements, and any pending formal or informal government examinations, investigations or prosecutions by any federal or state governmental or regulatory body or agency, or any industry regulatory authority.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.9.</span>
                                  &nbsp;
                                  <b>No Breach.</b>
                                  &nbsp;Neither the execution, delivery or performance of this Agreement, nor the consummation of the transaction contemplated hereby, shall conflict with, result in a violation or breach of, or require the consent of any person under the terms, conditions or provisions of any contract, notice, indenture, license, permit, lease or other instrument to which Merchant is a party or any judgment, decree, or order to which Merchant is subject.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.10.</span>
                                  &nbsp;
                                  <b>Solvency.</b>
                                  &nbsp;Merchant is solvent, is subject to neither insolvency or bankruptcy proceedings nor petitions to initiate such proceedings, nor orders issued in such proceedings, and legal grounds do not exist for such proceedings, petition, or order.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.11.</span>
                                  &nbsp;
                                  <b>Authority Customer Data.</b>
                                  &nbsp;Merchant has the right to provide to Program Administrator any Customer Data, free of any obligation of confidentiality to any third-party, and that such information is accurate and, if pertaining to any Customer, has been collected and submitted with such Customer’s consent.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.12.</span>
                                  &nbsp;
                                  <b>No Authority to Act for Customer.</b>
                                  &nbsp;Merchant is prohibited from accepting Loan disclosures, providing consent, or executing any documents required under the Program on behalf of Customers without Program Administrator approval.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.13.</span>
                                  &nbsp;
                                  <b>No Employee or Owner Financing.</b>
                                  &nbsp;Merchant shall not apply for or obtain a Loan under the Loan Program to finance the purchase of goods or services offered or sold by Merchant. Merchant shall ensure that all of its agents, employees, owners, relatives of owners, and Affiliates are prohibited from applying for or obtaining a Loan under the Loan Program to finance the purchase of goods or services offered or sold by Merchant.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.14.</span>
                                  &nbsp;
                                  <b>No Duty For Program Administrator to Verify, Validate, or Correct.</b>
                                  &nbsp;Merchant understands, acknowledges and agrees that Program Administrator has no obligation to verify, validate, or correct information provided by Merchant or Customers in any way. Notwithstanding the provisions of the immediately preceding sentence, Program Administrator may in its sole discretion review the information provided by Merchant or Customers and, at any time and in its sole discretion, including without limitation, upon an inquiry or request from Funding Participant or federal or state governmental or regulatory body or agency, or any industry regulatory authority, either (i) request any changes or amendments thereto, with which Merchant undertakes to comply, or (ii) remove the information provided by Merchant or Customers from the Merchant Platform.  The Merchant shall have no claim against Program Administrator and shall not be entitled to any remedy from Program Administrator in connection with such actions.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.15.</span>
                                  &nbsp;
                                  <b>Program Administrator is Not a Lender of Record.</b>
                                  &nbsp;Merchant understands, acknowledges and agrees that the Funding Participant is the lender of record and Program Administrator (i) is not a lender, (ii) does not make lending decisions, (iii) does not offer to lend, (iv) has no involvement with the underwriting of a financial transaction, (v) makes no representation that any Customer will be approved for consumer financing or that a certain percentage of Customers will be approved for consumer financing, and (vi) does not have authority as an agent, representative or broker of Funding Participant to extend credit on behalf of that Funding Participant.  Merchant also understands, acknowledges and agrees that neither Merchant nor any Customer shall have any claim against Program Administrator or be entitled to any remedy from Program Administrator in connection with any rejection or denial of any Loan Application or failure by Funding Participant to fund any amount required under a Loan Agreement.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.16.</span>
                                  &nbsp;
                                  <b>Merchant Platform.</b>
                                  &nbsp;Merchant understands, acknowledges and agrees that Program Administrator shall not be liable for the inadvertent disclosure, corruption or erasure of data (including but not limited to data provided by Merchant or Customer), transmitted, received or stored on the Merchant Platform, or any part thereof, except to the extent such disclosure, corruption or erasure can be attributed to a fault of the Merchant Platform, or the hardware on which the Merchant Platform, or the data transmitted through it, is hosted and stored.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.17.</span>
                                  &nbsp;
                                  <b>Holder in Due Course Rule.</b>
                                  &nbsp;All representations made by Merchant to Customers related to the goods and services sold and financed under this Agreement are and will be complete and correct. The quality of the goods and services sold or to be sold meets or exceeds applicable standards sufficient to ensure that (a) Customers will have no claims against Merchant (i) for unfair or deceptive trade practices and (ii) under the Federal Trade Commission’s (“
                                  <u>FTC</u>
                                  ”) Rule Regarding Preservation of Consumers’ Claims and Defense (Holder in Due Course Rule) and its state law counterparts, and (b) Merchant has done, and will do, no act or thing which may adversely affect the enforceability of the Loans.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.18.</span>
                                  &nbsp;
                                  <b>Merchant Representations.</b>
                                  &nbsp;Merchant understands, acknowledges and agrees that it is responsible for updating any disclosures it has made to Program Administrator which, if not disclosed, would make any of the above representations, warranties or covenants incorrect as of the date of submission of any Loan Application or funding of any Loan Agreement.  No representation, warranty or written statement made by Merchant in its Application, this Agreement or in any exhibit, schedule, written statement or certificate furnished to Program Administrator or Funding Participant in connection with the transactions contemplated hereby by Merchant contains or will contain any untrue statement of material fact or omits or will omit a material fact necessary to make the statements contained herein or therein not misleading.  Each of the foregoing representations and warranties by Merchant (i) shall be deemed to be repeated by Merchant at the time Loan Applications are submitted and at the time each Loan is funded, (ii) shall survive the execution and delivery of this Agreement and the making of Loans, and (iii) shall continue in full force and effect until all terms and conditions of the last Borrower has repaid his/her loan, in full, and this Agreement has been fully performed.  Program Administrator may rely upon the foregoing representations and warranties of Merchant irrespective of any information or knowledge obtained by Program Administrator of anything contrary to or inconsistent therewith.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.19.</span>
                                  &nbsp;
                                  <b>Non-Contravention.</b>
                                  &nbsp;The execution, delivery and performance of this Agreement by Merchant, and the consummation by Merchant of the transactions contemplated hereby, (i) have been duly authorized by all necessary action on the part of Merchant, (ii) do not contravene any government or contractual restriction applicable to Merchant, and (iii) will not violate any agreement it has with third parties.  Merchant will advise Program Administrator promptly of any event that may adversely affect its prospects or continued operations.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">12.20.</span>
                                  &nbsp;
                                  <b>Binding Agreement.</b>
                                  &nbsp;This Agreement has been duly executed and delivered by Merchant and, assuming the due authorization, execution and delivery by Program Administrator, constitutes a legal, valid and binding obligation of Merchant enforceable against Merchant in accordance with its terms.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">13.</span>
                              &nbsp;
                              <b>Confidential Information.</b>
                            </p>

                            <ul>
                              <li>
                                <p>
                                  <span className="count">13.1.</span>
                                  &nbsp;From time to time, in connection with the operation of this Agreement and/or the transactions contemplated by this Agreement, one party (“
                                  <u>Discloser</u>
                                  ”) may disclose Confidential Information (defined below) to the other party (“
                                  <u>Recipient</u>
                                  ”), whether in writing, orally, or by allowing inspection of tangible objects (i.e., documents, tapes disks, prototypes, samples, plans or equipment).
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">13.2.</span>
                                  &nbsp;Recipient shall:
                                </p>
                                <ul>
                                  <li>
                                    <p>
                                      <span className="count">13.2.1.</span>
                                      &nbsp;Hold Confidential Information in the strictest confidence and use it solely (i) to carry out the transactions anticipated by this Agreement and fulfill its obligations hereunder or (ii) with respect to Loan Confidential Information held by or on behalf of Funding Participant, as is reasonably necessary, to establish, maintain and enforce such Funding Participant’s rights with respect to such Loans, subject to
                                      &nbsp;
                                      <u>Section 13.2.2</u>
                                      ;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">13.2.2.</span>
                                      &nbsp;Disclose Confidential Information only to its personnel and third-party service providers  (including a Funding Participant, its designated Sponsor, successors and assigns) who need to receive such Confidential Information; provided that Recipient must: (i) inform any such person or service provider of the confidential nature of such Confidential Information; (ii) take commercially reasonable steps to ensure that any such person and service provider does not violate the provisions of this
                                      &nbsp;
                                      <u>Section 13</u>
                                      ; and (iii) immediately notify Discloser if Recipient has reason to believe any such personnel or service provider has violated or intends to violate the provisions of this
                                      &nbsp;
                                      <u>Section 13</u>
                                      ; provided further that Recipient will be liable for any acts or omissions of any such service provider or personnel in breach of this
                                      &nbsp;
                                      <u>Section 13</u>
                                      ;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">13.2.3.</span>
                                      &nbsp;Not reverse engineer, disassemble or decompile any prototypes, software or other tangible objects embodying Confidential Information;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">13.2.4.</span>
                                      &nbsp;Not make any copies of Confidential Information unless previously authorized in writing by Discloser, except that Funding Participant may make copies of Confidential Information related to specific Loan Applications or Loan Agreements subject to
                                      &nbsp;
                                      <u>Section 13.3</u>
                                      ;
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">13.2.5.</span>
                                      &nbsp;If authorized to make copies of Confidential Information, reproduce on such copies any proprietary rights and/or confidentiality notices appearing on the original Confidential Information in the same manner as on the original; and
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">13.2.6.</span>
                                      &nbsp;Use its commercially reasonable best efforts to protect and maintain the confidentiality of the Confidential Information, which protections shall be at least equivalent in scope and effect to the measures taken by Recipient to protect its own confidential or proprietary information of a like or similar nature, but in no event less than a commercially reasonable level of such protection.
                                    </p>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <p>
                                  <span className="count">13.3.</span>
                                  &nbsp;Merchant agrees to protect Non-Public Personal Information (“
                                  <u>NPPI</u>
                                  ”) about the Customer as required by any applicable state or federal laws, regulations, ordinance or guidance, including the Privacy and Safeguards regulations (collectively, the “
                                  <u>Safeguarding Rules</u>
                                  ”). Merchant warrants and represents that it has and agrees to maintain physical, electronic, and procedural controls and safeguards in compliance with the FTC and Funding Participant’s Regulatory Agency Safeguarding Rules to protect NPPI from unwarranted disclosure. Merchant will implement and maintain appropriate measures designed to meet the following objectives: (i) to ensure the security and confidentiality of Customer NPPI; (ii) to protect against any anticipated threats or hazards to the security or integrity of such information; and (iii) to protect against unauthorized access to or use of such information that could result in substantial harm or inconvenience to any Customer. Merchant agrees that it shall comply with the FTC and Funding Participant’s Regulatory Agency Safeguarding Rules and will promptly notify Program Administrator of any breach of the Safeguarding Rules or breach of the provisions of this
                                  &nbsp;
                                  <u>Section 13</u>
                                  .
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">13.4.</span>
                                  &nbsp;Recipient may disclose Confidential Information belonging to Discloser to the extent required to be disclosed by applicable law, provided that (i) Recipient provides Discloser with written notice of such requirement to disclose as soon as practicable once learning of such requirement, if such notice is not prohibited by applicable law, (ii) consults with Discloser on the advisability of seeking confidential treatment for some or all of the Confidential Information at issue, and (iii) assists Discloser as reasonably requested by Discloser, and at Recipient’s expense, in obtaining a protective order or otherwise securing confidential treatment for such Confidential Information.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">13.5.</span>
                                  &nbsp;Recipient shall return or destroy, as Discloser indicates, all Confidential Information belonging to Discloser, including without limitation all copies, compilations, summaries, analyses or other materials containing or reflecting Recipient’s use of Confidential Information, within ten (10) days after the earlier of (i) termination of this Agreement or (ii) Discloser’s written request to Recipient for such return or destruction. Recipient shall promptly send Discloser written certification of such destruction or return.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">13.6.</span>
                                  &nbsp;Except for Customer Data related to Loan Applications or Loan Agreements subject to
                                  &nbsp;
                                  <u>Section 4.10</u>
                                  , the Confidential Information (and related copies and materials) shall be the sole and exclusive property of the Discloser thereof.  Recipient has no rights under any of Discloser’s patents, copyrights, trademarks, trade secrets or with respect to any of Discloser’s other intellectual property, except if and as expressly set forth herein. Recipient may not use Confidential Information to apply for or secure any patents or any other intellectual property rights.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">13.7.</span>
                                  &nbsp;As used in this Agreement, “
                                  <u>Confidential Information</u>
                                  ” means any proprietary information, technical data, demographic information, Customer Data, trade secrets or know-how, including but not limited to research, product plans, products, services, customers, customer lists, markets, software, developments, inventions, processes, formulas, technology, designs, drawings, engineering, hardware configuration information, marketing, finances or other business information disclosed by either Party either directly or indirectly in writing, orally or by drawings or inspection of parts or equipment but excludes any such information that: (a) was lawfully in a Party’s possession before receiving it from the other Party; (b) is provided in good faith to one Party by a third party without, to such Party’s knowledge, breaching any rights of the other Party; (c) is or becomes generally available to the public other than through a violation of this Merchant Agreement; or (d) was or is independently developed without use of or reference to the Confidential Information.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">14.</span>
                              &nbsp;
                              <b>Guidelines.</b>
                              &nbsp;Merchant shall satisfy all other requirements designated in any Guidelines or as otherwise may be required from time to time by Program Administrator, acting on behalf of Funding Participant, and communicated to Merchant.  The terms of the Guidelines are incorporated by reference into this Agreement.  In the event there is any inconsistency between any Guidelines and this Agreement, this Agreement shall govern.  Notwithstanding the provisions regarding notice in
                              &nbsp;
                              <u>Section 24.9</u>
                              , Merchant agrees that Program Administrator may post the Guidelines on the Program website and/or Merchant Platform and that doing so will constitute notice thereof to Merchant.  Merchant agrees that it has an ongoing obligation to check the website for any updates or changes to the Guidelines.
                            </p>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">15.</span>
                              &nbsp;
                              <b>Audit.</b>
                              &nbsp;Upon the giving of reasonable prior written notice, Merchant shall afford Program Administrator and Funding Participant and their authorized representatives, including internal and external auditors, and governmental entities with regulatory authority over either Party (collectively, the “
                              <u>Auditors</u>
                              ”),  access, during normal business hours, to the books and records and other information relating to the Program, and permit such Auditors to make copies as may be necessary for the purposes of auditing the Loan Program and compliance with applicable law. Auditors’ activities shall be conducted in a manner which does not unreasonably interfere with the normal operations, customers and employee relations of Merchant. Program Administrator and Funding Participant are entitled to inspect Merchant’s Confidential Information and Customer NPPI compliance with the security requirements in
                              &nbsp;
                              <u>Section 13</u>
                              .
                            </p>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">16.</span>
                              &nbsp;
                              <b>Disclaimer.</b>
                              &nbsp;EXCEPT AS OTHERWISE EXPRESSLY SET FORTH IN THIS AGREEMENT, PROGRAM ADMINISTRATOR MAKES NO REPRESENTATIONS OR WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION (I) ANY WARRANTY THAT THE PROVISION OF THE PROGRAM OR THE MERCHANT PLATFORM WILL BE UNINTERRUPTED, TIMELY, AVAILABLE, SECURE OR ERROR FREE; (II) ANY IMPLIED WARRANTY OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE; (III) ANY IMPLIED WARRANTY ARISING FROM COURSE OF DEALING OR COURSE OF PERFORMANCE; (IV) ANY WARRANTY AS TO THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE PROGRAM OR THE MERCHANT PLATFORM; (V) ANY WARRANTY TO THE QUALITY, ACCURACY, COMPLETENESS, RELIABILITY, CONTENT OR PERFORMANCE OF THE PROGRAM OR THE MERCHANT PLATFORM; OR (VI) THAT THE PROGRAM OR THE MERCHANT PLATFORM WILL MEET THE REQUIREMENTS OF MERCHANT, CUSTOMER OR THIRD PARTY.   PROGRAM ADMINISTRATOR DOES NOT REPRESENT OR WARRANT THAT DEFECTS WILL BE CORRECTED, OR THAT THE PROGRAM OR THE MERCHANT PLATFORM, OR THE SERVER THAT MAKES THE PROGRAM OR THE MERCHANT PLATFORM ARE FREE OF COMPUTER VIRUSES OR OTHER HARMFUL COMPONENTS. TO THE EXTENT THE ABOVE DISCLAIMERS ARE PROHIBITED BY LAW, THEIR APPLICABILITY SHALL BE LIMITED TO THE MINIMUM PERMITTED BY LAW.
                            </p>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">17.</span>
                              &nbsp;
                              <b>Indemnification.</b>
                            </p>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">17.1.</span>
                                  &nbsp;Merchant agrees to indemnify, defend (at Merchant’s sole expense, and with counsel selected by and acceptable to the applicable LendingUSA Indemnified Person as defined herein), and hold harmless Program Administrator and Funding Participant, as well as their respective Affiliates, officers, directors, shareholders, members, partners, managers, employees, representatives and agents (each a “
                                  <u>LendingUSA Indemnified Person</u>
                                  ”) from and against any and all losses, claims, investigations, litigation, proceedings, liabilities, damages, administrative charges and expenses (including attorneys’ fees) of any kind whatsoever (collectively a “
                                  <u>Loss</u>
                                  ”) directly or indirectly arising out of or related to Merchant’s breach of any obligation owed to Program Administrator or such other LendingUSA Indemnified Person, including: (i) breach of any representation, warranty or covenant of Merchant contained in this Agreement, including any addendum attached hereto; (ii) failure of Merchant to comply with any applicable federal, state or local law, rule, regulation or ordinance; (iii) any Loss sustained by or threatened against any LendingUSA Indemnified Person attributable in whole or in part to negligence, fraud, error (whether negligent or not), omission or misconduct of Merchant, its employees, subcontractors, representatives or agents; (iv) any Loss sustained by or threatened against any LendingUSA Indemnified Person by reason of, or attributable in whole or in part to, Merchant’s failure to perform any of its obligations, or discharge any of its responsibilities, to any person, including failure to pay Transaction Fees when due; (v) any defect in any products and services sold or provided by Merchant, or any breach of any express or implied warranty in connection with such products and services; (vi) any voluntary or involuntary bankruptcy or insolvency proceeding by or against Merchant; and (vii) any Loss sustained by or threatened against any LendingUSA Indemnified Person attributable in whole or in part to the collecting and/or refunding of the Deposit; provided that Merchant shall have no obligations or liability under this
                                  &nbsp;
                                  <u>Section 17</u>
                                  &nbsp;
                                  to the extent a Loss results solely from the gross negligence or willful misconduct of a LendingUSA Indemnified Person.  Merchant further agrees to reimburse each LendingUSA Indemnified Person upon demand for all legal and other expenses (including expenses related to investigation, settlement, compromise or satisfaction) incurred by any such LendingUSA Indemnified Person in connection with any of the foregoing.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">17.2.</span>
                                  &nbsp;Merchant agrees that if it breaches any representation or warranty herein or if a Borrower asserts any claim or defense (regardless of the validity thereof) arising out of any transaction evidenced by any Loan Application, Loan Agreement or Transaction Request or cancels any transaction evidenced by any Loan Application, Loan Agreement or Transaction Request, Merchant will refund on demand the amount of any Loan affected, plus any finance or other charges related to such Loan.  Merchant also agrees to indemnify and hold the LendingUSA Indemnified Persons harmless for any and all breaches of warranties, damages and costs, including attorneys’ fees, which any LendingUSA Indemnified Person may sustain as a result of any such event. Program Administrator, on its own behalf or on behalf of Funding Participant, may, at its option, deduct any amount Merchant owes Program Administrator or Funding Participant pursuant to this
                                  &nbsp;
                                  <u>Section 17.2</u>
                                  &nbsp;
                                  or any other provision of this Agreement from any amount Program Administrator or Funding Participant may owe Merchant.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">17.3.</span>
                                  &nbsp;In the event and for so long as any LendingUSA Indemnified Person actively is contesting or defending against any action, suit, proceeding, hearing, investigation, charge, complaint, claim, or demand in connection with (i) any transaction contemplated under this Agreement or (ii) any fact, situation, circumstance, status, condition, activity, practice, plan, occurrence, event, incident, action, failure to act, or transaction involving Merchant or any Customer of Merchant, Merchant will cooperate with such LendingUSA Indemnified Person and its counsel with respect thereto, make available any personnel under its control, and provide such testimony and access to its books and records, including allowing copies to be made by such LendingUSA Indemnified Person or its representatives, as shall be reasonably necessary in connection therewith, all at the sole cost and expense of Merchant.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">18.</span>
                              &nbsp;
                              <b>Limitation of Liability.</b>
                            </p>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">18.1.</span>
                                  &nbsp;THE PROGRAM AND THE MERCHANT PLATFORM ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS. TO THE FULLEST EXTENT PERMITTED BY LAW, PROGRAM ADMINISTRATOR, EACH FUNDING PARTICIPANT AND THEIR RESPECTIVE AGENTS, EMPLOYEES, MEMBERS, MANAGERS, OFFICERS, DIRECTORS, SUCCESSORS AND ASSIGNS, SHALL NOT BE LIABLE FOR ANY SPECIAL, INDIRECT, INCIDENTAL, CONSEQUENTIAL OR PUNITIVE DAMAGES (INCLUDING LOSS OF PROFITS, BUSINESS OR DATA), WHETHER BASED ON BREACH OF CONTRACT, WARRANTY, TORT (INCLUDING NEGLIGENCE) OR OTHERWISE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH LOSS OR DAMAGE, OR IF SUCH LOSS OR DAMAGE COULD HAVE BEEN REASONABLY FORESEEN. THE LIABILITY PROVISIONS OF THIS AGREEMENT SHALL SURVIVE ITS EXPIRATION OR TERMINATION UNTIL ALL CLAIMS CONTEMPLATED BY THESE PROVISIONS ARE FULLY, FINALLY AND ABSOLUTELY BARRED BY THE APPLICABLE STATUTE OF LIMITATIONS.
                                </p>
                              </li>
                              <ul>
                                <li>
                                  <p>
                                    <span className="count">18.2.</span>
                                    &nbsp;The total aggregate liability of Program Administrator to Merchant or its respective agents, employees, managers, members, officers, successors or assigns, in respect of all claims, losses or damages, whether based on an action or claim in contract, tort (including negligence), breach of statutory duty, under an indemnity or otherwise, arising out of, or in connection with, this Agreement, or the use of the Program or the Merchant Platform, will be limited in aggregate to the total amount paid by Merchant to Program Administrator during the twelve (12) consecutive months preceding the date of the claim.
                                  </p>
                                </li>
                              </ul>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">19.</span>
                              &nbsp;
                              <b>Complaints and Demands.</b>
                              &nbsp;Within two (2) Business Days of receipt, Merchant shall provide Program Administrator with a copy of any written complaint or demands, or a report of any verbal complaint or demand received from any Borrower or any third party, including any regulatory authority.  Merchant agrees it will cooperate with Program Administrator and Funding Participant in responding to complaints, which cooperation may include providing documents evidencing applicant or Borrower authorization to submit a Loan Application or transaction.
                            </p>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">20.</span>
                              &nbsp;
                              <b>Term and Termination.</b>
                            </p>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">20.1.</span>
                                  &nbsp;This Agreement is effective in perpetuity, and the Program will begin upon acceptance of Merchant by Program Administrator, unless and until terminated in accordance with this
                                  &nbsp;
                                  <u>Section 20</u>
                                  .  The termination of this Agreement shall not affect the rights of either Party to recover for breaches occurring (or with respect to matters relating to Loans originated) prior thereto or with respect to provisions of this Agreement that by the nature of their terms continue after termination, including
                                  &nbsp;
                                  <u>Section 17</u>
                                  .  Program Administrator may terminate this Agreement at any time, with or without cause. Merchant may terminate this Agreement upon providing at least thirty (30) days written notice to Program Administrator.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">20.2.</span>
                                  &nbsp;Notwithstanding termination of this Agreement, the provisions of this Agreement will continue in full force and effect as to all Loan Applications accepted or approved by Funding Participant under the Program prior to termination; provided, however, that for any pending transactions, neither Program Administrator nor Funding Participant will be obligated to accept such transaction.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">20.3.</span>
                                  &nbsp;Termination will not release either Party from financial obligations owed to the other Party for services previously delivered or payments owed prior to termination, and the Parties shall cooperate with each other to complete all outstanding obligations to Customers in a mutually agreed fashion.  Further, upon termination of this Agreement: (a) all authorizations and licenses granted by Program Administrator will terminate and all rights shall revert to Program Administrator; (b) each Party will immediately destroy or return to the other Party, and upon request, certify such destruction of, all of the other’s materials, documentation, data, and Confidential Information, including all related materials that were derived from such materials, documentation, data, and Confidential Information, as provided for in
                                  &nbsp;
                                  <u>Section 13</u>
                                  ; and (c) Merchant shall immediately cease to represent itself as participating in the Program and cease its use of any of the Program Administrator Marks.  Program Administrator’s name, logo and any other proprietary information related to Program Administrator and the Program will be removed immediately from the Merchant’s website, e-mail signature, marketing and promotional materials, offices and demonstration labs.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">20.4.</span>
                                  &nbsp;No termination of this Agreement will release either Party from any payment or other obligations owed to the other or affect any rights or liabilities of either Party with respect to any breach of this Agreement.  Merchant’s obligations under this Agreement shall survive in full force and effect until all the terms and conditions of this Agreement applicable to the last Borrower to repay his or her Loan in full have been fully performed, and all Chargebacks and other adjustments have been paid under the terms of this Agreement. For the avoidance of doubt and notwithstanding anything herein to the contrary, all of the expressly surviving terms of this Agreement, and all of the Merchant’s obligations under
                                  &nbsp;
                                  <u>Section 8</u>
                                  &nbsp;
                                  (Fees),
                                  &nbsp;
                                  <u>Section 9</u>
                                  &nbsp;
                                  (Merchant Settlement Account),
                                  &nbsp;
                                  <u>Section 10</u>
                                  &nbsp;
                                  (Returns and Chargebacks),
                                  &nbsp;
                                  <u>Section 11</u>
                                  &nbsp;
                                  (Intellectual Property),
                                  &nbsp;
                                  <u>Section 12</u>
                                  &nbsp;
                                  (Representations and Warranties),
                                  &nbsp;
                                  <u>Section 13</u>
                                  &nbsp;
                                  (Confidential Information),
                                  &nbsp;
                                  <u>Section 16</u>
                                  &nbsp;
                                  (Disclaimer),
                                  &nbsp;
                                  <u>Section 17</u>
                                  &nbsp;
                                  (Indemnification),
                                  &nbsp;
                                  <u>Section 18</u>
                                  &nbsp;
                                  (Limitation of Liability),
                                  &nbsp;
                                  <u>Section 21</u>
                                  &nbsp;
                                  (Governing Law; Injunctive Relief, Attorney Fees),
                                  &nbsp;
                                  <u>Section 23</u>
                                  &nbsp;
                                  (Waiver of Jury Trial), and
                                  &nbsp;
                                  <u>Section 24</u>
                                  &nbsp;
                                  (General Terms) shall survive and remain in full force and effect regardless of the consummation of the transactions contemplated hereby, repayment of the Loans, satisfaction of all Chargebacks and other adjustments, the enforcement, amendment or waiver of any provision of this Agreement, or the expiration or termination of this Agreement.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">20.5.</span>
                                  &nbsp;In the event of breach of this Agreement by either Party, the non-breaching Party will be entitled to exercise any and all rights and remedies as shall be available to it at law or in equity. The non-breaching Party may exercise remedies concurrently or separately, and the exercise of one remedy will not be deemed either an election of such remedy or a preclusion of the right to exercise any other remedy.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">21.</span>
                              &nbsp;
                              <b>Governing Law; Injunctive Relief; Attorney Fees.</b>
                            </p>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">21.1.</span>
                                  &nbsp;
                                  <b>Governing Law.</b>
                                  &nbsp;This Agreement shall be governed by and construed in accordance with the laws of the State of California without giving effect to principles of conflicts of law.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">21.2.</span>
                                  &nbsp;
                                  <b>Injunctive Relief.</b>
                                  &nbsp;Notwithstanding anything to the contrary in this Agreement, Program Administrator may seek injunctive or other relief in any state, federal, or national court of competent jurisdiction for any actual or alleged infringement of the intellectual property or proprietary rights of Program Administrator or any other person or entity.  Merchant further acknowledges and agrees that Program Administrator’s rights in the Program and Program Administrator IP are of a special, unique, extraordinary character, giving them peculiar value, the loss of which cannot be readily estimated or adequately compensated for in monetary damages.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">21.3.</span>
                                  &nbsp;
                                  <b>Attorney Fees.</b>
                                  &nbsp;Merchant agrees to reimburse Program Administrator for any reasonable attorney’s fees and costs Program Administrator incurs in successfully defending or prosecuting a claim against Merchant which results from any breach by Merchant of this Agreement.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">22.</span>
                              &nbsp;
                              <b>Arbitration.  MERCHANT ACKNOWLEDGES THAT IT HAS CAREFULLY READ THIS SECTION AND UNDERSTANDS THAT IT LIMITS MERCHANT’S RIGHTS IN THE EVENT OF A DISPUTE BETWEEN MERCHANT AND PROGRAM ADMINISTRATOR. MERCHANT UNDERSTANDS THAT IT HAS THE RIGHT TO REJECT THIS PROVISION AS PROVIDED IN SECTION 22.7 BELOW. </b>
                            </p>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">22.1.</span>
                                  &nbsp; The Parties to this Agreement understand and agree that either Party may, at its sole election, require that the sole and exclusive forum and remedy for resolution of a Claim (as defined herein) be final and binding arbitration pursuant to this
                                  &nbsp;
                                  <u>Section 22</u>
                                  &nbsp;
                                  (the &quot;
                                  <u>Arbitration Provision</u>
                                  &quot;). For the purposes of this Arbitration Provision, &quot;
                                  <u>Claim</u>
                                  &quot; shall include any past, present, or future claim, dispute, or controversy involving Merchant and Program Administrator that is relating to or arising out of this Agreement, including any claim challenging the validity or enforceability of this Arbitration Provision, in whole or in part, or the validity or enforceability of this entire Agreement. Claims are subject to arbitration regardless of whether they arise from contract, tort (intentional or otherwise), statute, common law, principles of equity, or otherwise. Claims include matters arising as initial claims, counter-claims, cross-claims, third-party claims, or otherwise. The scope of this Arbitration Provision is to be given the broadest possible interpretation that is enforceable.
                                </p>
                              </li>

                              <li>
                                <p>
                                  <span className="count">22.2.</span>
                                  &nbsp;The Party requesting arbitration shall do so by engaging the Judicial Alternatives and Mediation Services (&quot;
                                  <u>JAMS</u>
                                  &quot;). The arbitration, as well as the location of the arbitration, shall be conducted in accordance with the rules and policies of the administrator except to the extent the rules conflict with this Arbitration Provision or any countervailing law. Merchant may call 1 (800) 352-5267 or visit the JAMS web site at
                                  &nbsp;
                                  <a href="https://www.jamsadr.com" target="_blank" rel="noreferrer"><u>www.jamsadr.com</u></a>
                                  &nbsp;
                                  to find further information on JAMS and the arbitration process. In the case of a conflict between the rules and policies of the administrator and this Arbitration Provision, this Arbitration Provision shall control, subject to countervailing law, unless all Parties to the arbitration consent to have the rules and policies of the administrator apply.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">22.3.</span>
                                  &nbsp; If Program Administrator elects arbitration, it shall pay the administrator’s filing costs and administrative fees (other than hearing fees). If Merchant elects arbitration, filing costs and administrative fees (other than hearing fees) shall be paid in accordance with the rules of the administrator, or in accordance with countervailing law if in conflict with the administrator’s rules. Program Administrator will pay the administrator’s hearing fees for one (1) full day of hearings.  Fees for subsequent hearing days will be paid by the Party requesting the hearing, unless the administrator’s rules or applicable law require otherwise, or Merchant requests that Program Administrator pay them and Program Administrator agrees to do so. Each Party shall bear the expense of its own attorneys’ fees, except as otherwise provided by law or Section 21.3 of this Agreement. If a statute gives Merchant the right to recover any of these fees, these statutory rights shall apply in the arbitration notwithstanding anything to the contrary herein.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">22.4.</span>
                                  &nbsp;Within thirty (30) days of a final award by the arbitrator, any Party may appeal the award for reconsideration by a three-arbitrator panel selected according to the rules of the arbitrator administrator. In the event of such an appeal, any opposing Party may cross-appeal within thirty (30) days after notice of the appeal. The panel will reconsider de novo all aspects of the initial award that are appealed. Costs and conduct of any appeal shall be governed by this Arbitration Provision and the administrator’s rules, in the same way as the initial arbitration proceeding. Any award by the individual arbitrator that is not subject to appeal, and any panel award on appeal, shall be final and binding, except for any appeal right under the Federal Arbitration Act (&quot;
                                  <u>FAA</u>
                                  &quot;), and may be entered as a judgment in any court of competent jurisdiction.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">22.5.</span>
                                  &nbsp;THE PARTIES AGREE THAT EACH MAY ONLY BRING CLAIMS AGAINST THE OTHER IN AN INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING. Unless consented to in writing by all Parties to the arbitration, no Party to the arbitration may join, consolidate, or otherwise bring claims for or on behalf of two (2) or more individuals or unrelated corporate entities in the same arbitration. Unless consented to in writing by all Parties to the arbitration, an award in arbitration shall determine the rights and obligations of the named Parties only, and only with respect to the claims in arbitration, and shall not (i) determine the rights, obligations, or interests of anyone other than a named Party, or resolve any Claim of anyone other than a named Party or (ii) make an award for the benefit of, or against, anyone other than a named Party. No administrator or arbitrator shall have the power or authority to waive, modify, or fail to enforce this
                                  &nbsp;
                                  <u>Section 22.5</u>
                                  , and any attempt to do so, whether by rule, policy, arbitration decision or otherwise, shall be invalid and unenforceable. Any challenge to the validity of this
                                  &nbsp;
                                  <u>Section 22.5</u>
                                  &nbsp;
                                  shall be determined exclusively by a court and not by the administrator or any arbitrator.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">22.6.</span>
                                  &nbsp;This Arbitration Provision shall survive (i) suspension, termination, revocation, assignments or amendments to this Agreement and the relationship of the Parties, and (ii) the bankruptcy or insolvency of any Party or other person. If any portion of this Arbitration Provision other than
                                  &nbsp;
                                  <u>Section 22.5</u>
                                  &nbsp;
                                  is deemed invalid or unenforceable, the remaining portions of this Arbitration Provision shall nevertheless remain valid and in force. If
                                  &nbsp;
                                  <u>Section 22.5</u>
                                  &nbsp;
                                  is deemed invalid or unenforceable for any reason by a court of competent jurisdiction, in whole or in part, then this Arbitration Provision shall not apply and all disputes in that instance shall be litigated in a court of competent jurisdiction.  In no event shall any invalidation be deemed to authorize an arbitrator to determine Claims or make awards beyond those authorized in this Arbitration Provision.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">22.7.</span>
                                  &nbsp;Merchant understands that it may opt out of this Arbitration Provision for all purposes by mailing written notice to the following address: LendingUSA, LLC, 15303 Ventura Blvd, Suite 850 Sherman Oaks, CA 91403 Attention: Legal Department. To reject this Arbitration Provision, Merchant must send written notice of its rejection to the above address within thirty (30) days of the date on which it entered into this Agreement.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">22.8.</span>
                                  &nbsp;This
                                  &nbsp;
                                  <u>Section 22</u>
                                  &nbsp;
                                  shall not apply to covered borrowers as defined in the Military Lending Act.
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">23.</span>
                              &nbsp;
                              <b>Waiver of Jury Trial.</b>
                              &nbsp;
                              <b>LENDINGUSA INDEMNIFIED PERSONS AND MERCHANT HEREBY KNOWINGLY, VOLUNTARILY AND INTENTIONALLY WAIVE ANY RIGHT TO A TRIAL BY JURY IN ANY ACTION, SUIT, PROCEEDING OR COUNTERCLAIM ARISING OUT OF OR RELATING TO THIS AGREEMENT OR THE TRANSACTIONS CONTEMPLATED HEREBY, ANY RELATED DOCUMENT OR UNDER ANY OTHER DOCUMENT OR AGREEMENT DELIVERED OR WHICH MAY IN THE FUTURE BE DELIVERED IN CONNECTION HEREWITH OR THEREWITH, OR ARISING FROM ANY RELATIONSHIP EXISTING IN CONNECTION WITH THIS AGREEMENT.</b>
                            </p>
                          </li>
                        </ul>

                        <ul className="list">
                          <li>
                            <p>
                              <span className="count">24.</span>
                              &nbsp;
                              <b>General Terms.</b>
                            </p>
                            <ul>
                              <li>
                                <p>
                                  <span className="count">24.1.</span>
                                  &nbsp;
                                  <b>Independent Contractors.</b>
                                  &nbsp;The Parties shall at all times be independent contractors with respect to each other, and nothing herein renders them partners, joint venturers nor employer or employee.  Each Party shall be solely responsible for its own employees in connection with performance under this Agreement, and all salary, wages, and benefits owed thereto.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.2.</span>
                                  &nbsp;
                                  <b>Binding Effect; Assignment.</b>
                                  &nbsp;Merchant may not assign this Agreement without the prior written consent of Program Administrator and any purported assignment without such consent shall be void.  Program Administrator and Funding Participant may assign this Agreement and any of their rights or obligations hereunder at any time.  In the event of such assignment, the assignee thereof shall have the same rights and remedies as any assignor under this Agreement, provided that such assignor shall not be relieved of its obligations hereunder arising prior to such assignment unless such assignment is part of an assignment of all or substantially all of its assets and the assignee assumes its obligations hereunder.  Otherwise, this Agreement is binding upon the Parties and their successors and assigns.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.3.</span>
                                  &nbsp;
                                  <b>
                                    Third Party Beneficiaries.  Merchant acknowledges that Program Administrator will enter into agreements with Funding Participant to provide financing under the Program and that such persons will originate and own the Loans contemplated hereby. Each Funding Participant (or its successors and assigns as maker or owner of such Loans) shall be a third party beneficiary of the obligations of Merchant hereunder and shall have the benefit of such obligations and the right to enforce (but not to the exclusion of Program Administrator for its own account) such rights, obligations, and remedies with respect to any Loan(s), Loan Agreement, and Loan Application with respect to which it is the  Funding Participant as if a party to this Agreement, regardless of whether Funding Participant (or its successors and assigns, or designated Sponsor) are referenced in a particular provision of this Agreement.  Except as otherwise provided for in
                                    &nbsp;
                                    <u>Section 2</u>
                                    &nbsp;
                                    and
                                    &nbsp;
                                    <u>Section 24.3</u>
                                    , nothing in this Agreement is intended to confer on any person other than the Parties specified hereto and their successors and assigns, any rights, obligations, remedies or liabilities.
                                  </b>
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.4.</span>
                                  &nbsp;
                                  <b>Rights of Persons Not a Party.</b>
                                  &nbsp;Except as expressly provided herein, this Agreement shall not create any rights on the part of any person not a party hereto, whether as a third-party beneficiary or otherwise.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.5.</span>
                                  &nbsp;
                                  <b>Insurance.</b>
                                  &nbsp;During the term of this Agreement and thereafter for so long as Merchant has any obligations with respect to the Program, Merchant shall maintain at its expense insurance in such amount and against such risks as is customary for businesses of a comparable size in the industry in which Merchant operates.   Insurance coverage shall be issued by a carrier rated “A VIII” or higher by A.M. Best or that otherwise is reasonably acceptable to Program Administrator, which acceptance will not be unreasonably withheld.  If requested by Program Administrator or Funding Participant, Program Administrator and Funding Participant shall be named as additional insureds under each policy. If requested by Program Administrator acting on behalf of Funding Participant, Merchant shall provide Program Administrator with a certificate of insurance evidencing such insurance coverage and renewals thereof. Merchant shall notify Program Administrator if any required insurance policy is cancelled, not renewed or modified in any material respect within fifteen (15) days of any such cancellation, non-renewal or modification.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.6.</span>
                                  &nbsp;
                                  <b>Employee Actions.</b>
                                  &nbsp;Each Party is responsible for the actions of its employees. In the event employment of an employee is terminated, the Party that employed such former employee will take reasonable steps to ensure that such former employee no longer has access to the Program systems (including changing any passwords necessary to access such information or system or any confidential information relating to, or arising from, the Program).
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.7.</span>
                                  &nbsp;
                                  <b>Marks.</b>
                                  &nbsp;Merchant hereby grants Program Administrator, on behalf of itself and Funding Participant, a non-exclusive license to use its name, trademarks, logos and other marks in connection with the administration and operation of the Program during and after the term of this Agreement.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.8.</span>
                                  &nbsp;
                                  <b>Complete Agreement.</b>
                                  &nbsp;This Agreement incorporates, and Merchant agrees to comply with, the most up-to-date version of all policies, appendices, specifications, guidelines, schedules, and other rules referenced in this Agreement or accessible on the Program portal, through LendingUSA or the Program website (as applicable), including Guidelines (“
                                  <u>Loan Program Policies</u>
                                  ”) and any updates of the Loan Program Policies from time to time.  In the event of any conflict between this Agreement and any Loan Program Policy, this Agreement will control.  In the event of a conflict between this Agreement and Merchant’s agreement with Program Administrator under a separate affiliate marketing program, that agreement will control with respect to such separate program. This Agreement (including the Loan Program Policies) is the entire agreement between Merchant and Program Administrator regarding the Program and supersedes all prior agreements and discussions. This Agreement shall not be superseded by any online merchant agreement that is electronically signed by Merchant when submitting its online Application through Program Administrator’s designated merchant enrollment application website, even if such a merchant agreement is executed after the Effective Date of this Agreement.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.9.</span>
                                  &nbsp;
                                  <b>Notices and Communication; Additional Provisions.</b>
                                </p>
                                <ul>
                                  <li>
                                    <p>
                                      <span className="count">24.9.1.</span>
                                      &nbsp;All demands, notices and communications shall be in writing.  Notices shall be deemed to have been duly given (i) three (3) Business Days from the date of mailing by regular first class U.S. mail, (ii) one (1) Business Day from the date of mailing by a commercial overnight carrier (providing proof of delivery), or (iii) the Business Day on which notice is sent by e-mail, provided that notice shall not be deemed to have been duly given to any Party with respect to which the notifying Party has received an indication the email was not actually delivered to such intended Party.  For purposes of this
                                      &nbsp;
                                      <u>Section 24.9.1</u>
                                      , Saturdays, Sundays and federal holidays shall be considered non-Business Days.
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">24.9.2.</span>
                                      &nbsp;All notices to Merchant shall be sent to Merchant’s postal or street address, or e-mail address set forth in the Application or such other address, or e-mail address as Merchant may advise Program Administrator in writing.  All notices to Program Administrator and Funding Participant hereunder shall be sent to the address or e-mail address set forth below or to such other address, or e-mail address as Program Administrator may advise Merchant in writing.
                                      <br />
                                      <br />
                                      LENDINGUSA, LLC
                                      <br />
                                      ATTN: LEGAL DEPARTMENT
                                      <br />
                                      15303 VENTURA BLVD., SUITE 850
                                      <br />
                                      SHERMAN OAKS, CA 91403
                                      <br />
                                      E-mail: gc@lendingusa.com
                                      <br />
                                    </p>
                                  </li>
                                  <li>
                                    <p>
                                      <span className="count">24.9.3.</span>
                                      &nbsp;By accepting this Agreement, Merchant hereby consents to Program Administrator and Funding Participant, as applicable: (i) sending Merchant, emails, telephone calls, and/or text messages relating to the Program and the Loan Program, from time to time; (ii) monitoring, recording, using, and disclosing information about Merchant and its Customers that Program Administrator obtains in connection with Merchant’s participation in the Program; (iii) reviewing, monitoring, web crawling, and otherwise investigating Merchant’s, including any websites’, compliance with this Agreement; and (iv) using, reproducing, distributing, and displaying Merchant’s implementation of the Loan Program as examples of best practices in Program Administrator’s educational materials.  For a Merchant that submits a personal telephone number in the Application, Merchant may discontinue the receipt of telephone calls or text messages to the personal telephone number by providing written notice to Program Administrator pursuant to
                                      &nbsp;
                                      <u>Section 24.9.2</u>
                                      &nbsp;
                                      advising Program Administrator that it is revoking its consent to receive telephone call and/or text messages to the personal telephone number.  The Parties understand that the Funding Participant have an interest in receiving notices and information which Merchant is required to share with Program Administrator pursuant to this Agreement. Accordingly, if a Funding Participant requests from Merchant in writing additional copies of required notices, Merchant shall provide such notices to the addresses provided Funding Participant.
                                    </p>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.10.</span>
                                  &nbsp;
                                  <b>Non-Exclusive Activities.</b>
                                  &nbsp;Merchant acknowledges and agrees that Program Administrator and Funding Participants, including their respective Affiliates, may at any time, directly or indirectly, (i)  solicit consumers or other merchants (that may operate services that are similar or compete with Merchant) on terms that may differ from those contained in this Agreement, and (ii)  operate services that are similar to or compete with Merchant.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.11.</span>
                                  &nbsp;
                                  <b>Delay.</b>
                                  &nbsp;With the exception of Merchant’s payment obligations, neither Party shall be liable for delay or failure to perform any of its obligations hereunder to the extent that such delay or failure arises from any cause beyond that Party’s reasonable control.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.12.</span>
                                  &nbsp;
                                  <b>Construction.</b>
                                  &nbsp;Paragraph captions in this Agreement are for ease of reference only and shall be given no substantive or restrictive meaning or significance whatsoever.  Whenever used in this Agreement, the terms “include(s)”, “including”, and “for example” are used and intended without limitation.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.13.</span>
                                  &nbsp;
                                  <b>Mutual Construction.</b>
                                  &nbsp;This Agreement shall be deemed to have been drafted by both the Merchant and Program Administrator.  Therefore, any ambiguities shall not be construed against either Party.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.14.</span>
                                  &nbsp;
                                  <b>Severability; No Waiver of Rights.</b>
                                  &nbsp;If any provision of this Agreement is held to be invalid, illegal or unenforceable for any reason, such provision shall be limited or eliminated to the minimum extent necessary so that the remaining provisions and the Agreement shall continue in full force and effect. The failure of either Party to exercise any right given to it under this Agreement or to insist on strict compliance of any obligation under this Agreement shall not constitute a waiver of any right, including the right to insist on strict compliance in the future.
                                </p>
                              </li>
                              <li>
                                <p>
                                  <span className="count">24.15.</span>
                                  &nbsp;
                                  <b>Modification.</b>
                                  &nbsp;Program Administrator reserves the right to modify any of the terms and conditions contained in this Agreement (including those in any Loan Program Policies) at any time and in its sole discretion by posting a change notice, revised Agreement, or revised Loan Program Policies on the Program website or by sending notice of such modification to Merchant by email to the primary email address then-currently associated with Merchant’s account (any such change by email will be effective on the date specified in such email but will in no event be less than two [2] Business Days after the date the email is sent). MERCHANT’S CONTINUED PARTICIPATION IN THE PROGRAM FOLLOWING THE EFFECTIVE DATE OF SUCH NOTICE, OR A SUBMISSION OF AN APPLICATION FOR CONSUMER FINANCING THROUGH THE PROGRAM, WILL CONSTITUTE MERCHANT’S ACCEPTANCE OF THE MODIFICATIONS AND SIGNIFY MERCHANT’S AGREEMENT TO BE BOUND BY THEM. IF ANY MODIFICATION IS UNACCEPTABLE TO MERCHANT, MERCHANT’S ONLY RECOURSE IS TO TERMINATE THIS AGREEMENT IN ACCORDANCE WITH
                                  &nbsp;
                                  <u>SECTION 20</u>
                                  .
                                </p>
                              </li>
                            </ul>
                          </li>
                        </ul>
                        <p style={{ textAlign: 'center' }}>
                          <b>Last Updated: January 2023</b>
                        </p>

                        <p style={{ textAlign: 'center' }}>
                          <b>Version: 3.3 – Enrollment Page</b>
                        </p>
                      </Checkbox>
                    </Column>
                    <Column className="small-12 checkbox-row padded-bottom">
                      <Checkbox
                        label={['Check here to agree to a soft pull credit inquiry']}
                        name="discountPolicy"
                        onChange={handleCheckboxChange.bind(null, 'discountPolicy')}
                        isChecked={values.discountPolicy}
                        id="discountPolicy"
                        errorMessage={formErrors?.discountPolicy}
                        isButtonDisabled={false}
                        title="Soft Pull Credit Inquiry"
                      >
                        <div>
                          <p style={{ marginBottom: '0' }}>&nbsp;</p>
                        </div>

                        <p style={{ textAlign: 'center' }}>
                          <b>LENDINGUSA, LLC</b>
                        </p>

                        <p style={{ textAlign: 'center' }}>
                          <b>Soft Pull Credit Inquiry</b>
                        </p>

                        <p>
                          By submitting this application, you authorize LendingUSA, LLC to conduct a consumer soft credit inquiry from one of the credit reporting agencies. This soft credit inquiry will not affect your credit score. The purpose of the soft credit inquiry is to assist LendingUSA in evaluating your eligibility for participation in the LendingUSA, LLC Merchant Program.
                        </p>
                      </Checkbox>
                    </Column>
                  </Column>
                  <Column className="small-12 medium-5 padded-top">
                    <Input
                      label="Principal Owner E-Signature"
                      name="submittedBy.name"
                      value={values.submittedBy && values.submittedBy.name}
                      onChange={handleInputChange}
                      isRequired
                      hasError={!!formErrors['submittedBy.name']}
                      errorMessage={formErrors['submittedBy.name']}
                      onBlur={handleBlur}
                    />
                  </Column>
                  <Column className="small-12 medium-3 padded-top">
                    <Input
                      label="Job Title"
                      name="principalOwner.title"
                      value={values.principalOwner && values.principalOwner.title}
                      onChange={handleInputChange}
                      isRequired
                      hasError={!!formErrors['principalOwner.title']}
                      errorMessage={formErrors['principalOwner.title']}
                      onBlur={handleBlur}
                    />
                  </Column>
                  <Column className="small-12 medium-3 padded-top">
                    <Input
                      name="submittedDate"
                      value={getCurrentDate()}
                      label="Date"
                      className="current-date"
                      isDisabled
                    />
                  </Column>
                  <Column className="small-12 padded-top" style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      className={cn('button large arrow blue w-100', 'button', isLoading ? '' : 'arrow')}
                      onClick={handleSubmitForm.bind(null, values)}
                      isDisabled={!(values.merchantAgreement && values.discountPolicy)}
                      isLoading={isLoading}
                      id="bsubmit"
                    >
                      Submit Application
                    </Button>
                  </Column>
                  <Column className="small-12 padded-top" style={{ display: 'flex', justifyContent: 'center' }}>
                    <p className="description">By submitting this application, you agree not to disclose the Merchant Discount Fee (MDF), or any difference between the Payout Amount and the Financed Price to your Customers, and further agree not to impose the MDF on any Customer either by charging the Customer directly for the MDF or indirectly by charging a higher cost for your goods or services.</p>
                  </Column>
                </GridLayout>
              </FormGroup>
            </GridLayout>
          </div>
        </div>
      </section>
    </form>
  );
};

MerchantApply.propTypes = {
  className: PropTypes.string,
  validator: PropTypes.object.isRequired,
  ipAddress: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

MerchantApply.defaultProps = {
  className: '',
};

export default Validator(schema)(MerchantApply);
